import React, { useEffect, useState, useRef } from "react";
import Map, { Popup, ScaleControl } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import Papa from "papaparse";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { Source, Layer } from "react-map-gl/maplibre";
import { useLocation } from "react-router-dom";
import { predictSmallVesselSync } from "./predictSmallVesselSync";

const API_BASE = "https://noisemaps.marble.eu";
// https://noisemaps.marble.eu
// "http://localhost:3001"

const buttonStyle = {
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "14px",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
    transition: "background-color 0.3s ease"
};

const buttonStyleSidebar = {
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "18px",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
    transition: "background-color 0.3s ease"
};


const getShipDescription = (type) => {
    if (type >= 60 && type <= 69) return "Passenger vessel";
    if (type >= 70 && type <= 79) return "Cargo vessel";
    if (type >= 80 && type <= 89) return "Tanker";
    if (type === 30) return "Fishing vessel";
    if (type === 36) return "Sailing vessel";
    if (type === 37) return "Pleasure craft";
    return "Unknown vessel type";
};



function metersToLatDeg(meters) {
    return meters / 111320;
}

function metersToLngDeg(meters, atLatDeg) {
    const cosLat = Math.cos((atLatDeg * Math.PI) / 180);
    const safeCos = Math.max(Math.abs(cosLat), 0.1);
    return meters / (111320 * safeCos);
}

function expandBoundsByMeters(bounds, meters) {
    const midLat = (bounds.minLat + bounds.maxLat) / 2;
    const dLat = metersToLatDeg(meters);
    const dLng = metersToLngDeg(meters, midLat);

    return {
        minLat: bounds.minLat - dLat,
        maxLat: bounds.maxLat + dLat,
        minLng: bounds.minLng - dLng,
        maxLng: bounds.maxLng + dLng
    };
}

function getSelMaxRangeMeters(band) {
    return (
        band === "63Hz" ? 40000 :
        band === "125Hz" ? 35000 :
        25000
    );
}

function getFriendlySelErrorMessage(payload) {
    const err = payload?.error;

    if (!err) return "Error retrieving SEL heatmap.";

    if (typeof err === "string") {
        return err;
    }

    if (err.code === "SEL_ROWS_LIMIT") {
    return `AIS rows exceeded the limit of ${err.maxRows} points. Please reduce time period or area size.`;
}

    if (err.code === "SEL_DAYS_LIMIT") {
        return err.message || "Selected date range is too large.";
    }

    return err.message || "Error retrieving SEL heatmap.";
}

const ShipMap = () => {
    const [selectedShip, setSelectedShip] = useState(null);
    const navigate = useNavigate();
    const [shipData, setShipData] = useState([]);
    const [timeColumn, setTimeColumn] = useState("DATE TIME (UTC)");
    const [currentTime, setCurrentTime] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playSpeed, setPlaySpeed] = useState(500);
    const [zoomLevel, setZoomLevel] = useState(10);
    const [showHeatmapLegend, setShowHeatmapLegend] = useState(true);
    const [mapKey, setMapKey] = useState(0);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const mapRef = useRef(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [loginPassword, setLoginPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [selHeatmapGeoJSON, setSelHeatmapGeoJSON] = useState(null);
    const fetchController = useRef(null);
    const csvParserRef = useRef(null);
    const [activeTab, setActiveTab] = useState("controls1");

    const [regName, setRegName] = useState("");
    const [regEmail, setRegEmail] = useState("");
    const [regPassword, setRegPassword] = useState("");
    const [regError, setRegError] = useState("");

    const [showCalcModal, setShowCalcModal] = useState(false);
    const [calcLat, setCalcLat] = useState("");
    const [calcLng, setCalcLng] = useState("");
    const [calcDepth, setCalcDepth] = useState("");
    const [calcResult, setCalcResult] = useState(null);
    const [actualDepth, setActualDepth] = useState(null);
    const [depthError, setDepthError] = useState("");
    const [isPickingLocation, setIsPickingLocation] = useState(false);
    const pickHintRef = useRef(null);

    const [authMode, setAuthMode] = useState("login");
    const [loginEmail, setLoginEmail] = useState("");



    const [selectedBand, setSelectedBand] = useState("63Hz");
    const [selectedModel, setSelectedModel] = useState("AVERAGE");
    const [isSelectingArea, setIsSelectingArea] = useState(false);
    const [selectionStart, setSelectionStart] = useState(null);
    const [selectionEnd, setSelectionEnd] = useState(null);
    const [selectionCoords, setSelectionCoords] = useState(null);
    const [showSelectionModal, setShowSelectionModal] = useState(false);
    const [pendingSelGeneration, setPendingSelGeneration] = useState(false);
    const [pendingCsvFilter, setPendingCsvFilter] = useState(false);

    const [pendingSelExport, setPendingSelExport] = useState(false);
    const [pendingSelCsvGeneration, setPendingSelCsvGeneration] = useState(false);

    const [isLoggedIn, setIsLoggedIn] = useState(() => !!sessionStorage.getItem("jwtToken"));


    const [regSurname, setRegSurname] = useState("");
    const [regAffiliation, setRegAffiliation] = useState("");
    const [regIntendedUse, setRegIntendedUse] = useState("Academic use");
    const [showAdminPanel, setShowAdminPanel] = useState(false);
    const [adminUsers, setAdminUsers] = useState([]);
    const [adminError, setAdminError] = useState("");
    const [deleteUserId, setDeleteUserId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [captchaToken, setCaptchaToken] = useState("");
    const [regPasswordConfirm, setRegPasswordConfirm] = useState("");
    const [regMakeAdmin, setRegMakeAdmin] = useState(false);
    const [openedFromAdmin, setOpenedFromAdmin] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showRegisterSuccess, setShowRegisterSuccess] = useState(false);

const [lastSelBounds, setLastSelBounds] = useState(null);
const [selMin, setSelMin] = useState(null);
const [selMax, setSelMax] = useState(null);

const adaptiveSelMin = selMin != null ? Math.floor(selMin) : 150;
const adaptiveSelMax = selMax != null ? Math.ceil(selMax) : 180;
const adaptiveSelSpan = Math.max(adaptiveSelMax - adaptiveSelMin, 1);

const legendTicks = Array.from({ length: 6 }, (_, i) =>
    Math.round(adaptiveSelMin + (i * adaptiveSelSpan) / 5)
);

const selColorStops = [
    adaptiveSelMin + 0.00 * adaptiveSelSpan, "#00b300",
    adaptiveSelMin + 0.58 * adaptiveSelSpan, "#4fd000",
    adaptiveSelMin + 0.80 * adaptiveSelSpan, "#c8e600",
    adaptiveSelMin + 0.93 * adaptiveSelSpan, "#ffbf00",
    adaptiveSelMin + 0.985 * adaptiveSelSpan, "#ff6600",
    adaptiveSelMin + 1.00 * adaptiveSelSpan, "#cc0000"
];


    const openLogin = () => {
        setAuthMode("login");
        setLoginEmail("");
        setLoginPassword("");
        setLoginError("");
        setShowLoginModal(true);
    };

    const closeLoginModal = () => {
        setShowLoginModal(false);
        setAuthMode("login");
    };



    const loadUsers = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/users`);
            const data = await res.json();

            if (data.success) {
                setAdminUsers(data.users);
            } else {
                setAdminError("Failed to load users");
            }
        } catch (err) {
            setAdminError("Server error");
        }
    };


    const deleteUser = async (id) => {

        try {
            const res = await fetch(`${API_BASE}/api/delete-user`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id })
            });

            const data = await res.json();

            if (data.success) {
                setAdminUsers(prev => prev.filter(u => u.id !== id));
            } else {
                alert("Delete failed");
            }
        } catch (err) {
            alert("Server error");
        }
    };


    const hasSelData =
        selHeatmapGeoJSON &&
        selHeatmapGeoJSON.features &&
        selHeatmapGeoJSON.features.length > 0;
    const [availableCsvDaysSet, setAvailableCsvDaysSet] = useState(new Set());
    const [showMPA, setShowMPA] = useState(false);
    const [mpaLoading, setMpaLoading] = useState(false);


    const [csvData, setCsvData] = useState(null);
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setIsLoading(true);

        let rows = [];
        csvParserRef.current = Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            worker: true,
            step: function (result, parser) {
                rows.push(result.data);
            },
            complete: function () {
                csvParserRef.current = null;
                setIsLoading(false);

                const headers = Object.keys(rows[0] || {});
                const normalized = rows
                    .map(row => normalizeRow(row, headers))
                    .filter(row =>
                        row.LATITUDE !== undefined && !isNaN(row.LATITUDE) &&
                        row.LONGITUDE !== undefined && !isNaN(row.LONGITUDE)
                    );
                setCsvData(normalized);
                setShipData(normalized);
                setTimeColumn("timestamp");


                setPendingCsvFilter(true);
                setIsSelectingArea(true);
                setShowSelectionModal(true);

                const map = mapRef.current?.getMap?.();
                if (map) map.getCanvas().style.cursor = "crosshair";


                if (normalized.length > 0) {
                    const timestamps = normalized
                        .map(r => r.timestamp ? new Date(r.timestamp) : null)
                        .filter(Boolean)
                        .sort((a, b) => a - b);

                    if (timestamps.length > 0) {
                        setFromDate(timestamps[0]);
                        setToDate(timestamps[timestamps.length - 1]);
                    }
                }

                const daySet = new Set(
                    normalized
                        .map(r => r.timestamp ? new Date(r.timestamp) : null)
                        .filter(Boolean)
                        .map(date => {
                            const y = date.getUTCFullYear();
                            const m = String(date.getUTCMonth() + 1).padStart(2, "0");
                            const d = String(date.getUTCDate()).padStart(2, "0");
                            return `${y}-${m}-${d}`;
                        })
                );

                setAvailableCsvDaysSet(daySet);



            },
            error: function () {
                setIsLoading(false);
                csvParserRef.current = null;
                alert("Error parsing CSV!");
            }
        });

    };




    const fetchSelHeatmap = async (bounds = null) => {
        if (!fromDate || !toDate) {
            alert("Choose dates!");
            return null;
        }

        const days = (toDate - fromDate) / (1000 * 60 * 60 * 24);

        if (!isLoggedIn && days > 1) {
            alert("Guest users can generate SEL data for a maximum of 1 day.");
            return null;
        }

        setIsLoading(true);

        if (fetchController.current) fetchController.current.abort();

        const controller = new AbortController();
        fetchController.current = controller;

        const from = `${fromDate.getFullYear()}-${(fromDate.getMonth() + 1)
            .toString()
            .padStart(2, "0")}-${fromDate
                .getDate()
                .toString()
                .padStart(2, "0")} 00:00:00`;

        const to = `${toDate.getFullYear()}-${(toDate.getMonth() + 1)
            .toString()
            .padStart(2, "0")}-${toDate
                .getDate()
                .toString()
                .padStart(2, "0")} 23:59:59`;

        let boundsQuery = "";
        if (bounds) {
            boundsQuery = `&minLat=${bounds.minLat}&maxLat=${bounds.maxLat}&minLng=${bounds.minLng}&maxLng=${bounds.maxLng}`;
        }

        try {
    const res = await fetch(
        `${API_BASE}/api/sel-heatmap?from=${from}&to=${to}&model=${selectedModel}&band=${selectedBand}${boundsQuery}`,
        {
            signal: controller.signal,
            headers: {
                "X-User-Role": effectiveUserRole
            }
        }
    );

    const payload = await res.json();

    if (!res.ok) {
        throw new Error(getFriendlySelErrorMessage(payload));
    }

    setSelHeatmapGeoJSON(payload);
    return payload;

} catch (e) {
    if (e.name !== "AbortError") {
        alert(e.message || "Error retrieving SEL heatmap.");
    }
    return null;
} finally {
    setIsLoading(false);
    fetchController.current = null;
}
    };

    const fetchSelHeatmapFromCsv = async (rows, bounds = null, gridSize = 0.02) => {
        if (!rows || rows.length === 0) {
            alert("No CSV AIS rows available.");
            return null;
        }

        setIsLoading(true);

        if (fetchController.current) fetchController.current.abort();

        const controller = new AbortController();
        fetchController.current = controller;

        try {
            const compactRows = rows
                .filter(r =>
                    Number.isFinite(Number(r.LATITUDE)) &&
                    Number.isFinite(Number(r.LONGITUDE)) &&
                    Number.isFinite(Number(r.SPEED)) &&
                    Number(r.SPEED) > 0 &&
                    Number.isFinite(Number(r.A)) &&
                    Number.isFinite(Number(r.B)) &&
                    [0, 2, 3, 4, 7].includes(Number(r.NAVSTAT))
                )
                .map(r => ({
                    MMSI: r.MMSI,
                    LATITUDE: r.LATITUDE,
                    LONGITUDE: r.LONGITUDE,
                    SPEED: r.SPEED,
                    NAVSTAT: r.NAVSTAT,
                    AISTYPE: r.AISTYPE,
                    A: r.A,
                    B: r.B,
                    COURSE: r.COURSE,
                    HEADING: r.HEADING,
                    timestamp: r.timestamp
                }));

            const MAX_CSV_ROWS_HARD = 120000;
            const MAX_CSV_ROWS_FOR_PROCESSING = 20000;

            if (compactRows.length > MAX_CSV_ROWS_HARD) {
                alert("This CSV is too large for browser-upload SEL processing. Please split it into smaller time ranges.");
                setIsLoading(false);
                fetchController.current = null;
                return null;
            }

            if (compactRows.length === 0) {
                alert("No valid AIS rows remained after filtering.");
                setIsLoading(false);
                fetchController.current = null;
                return null;
            }

            let rowsToSend = compactRows;

            const body = {
    rows: rowsToSend,
    model: selectedModel,
    band: selectedBand,
    gridSize,
    from: fromDate
        ? `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, "0")}-${String(fromDate.getDate()).padStart(2, "0")} 00:00:00`
        : null,
    to: toDate
        ? `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, "0")}-${String(toDate.getDate()).padStart(2, "0")} 23:59:59`
        : null
};

            if (bounds) {
                body.minLat = bounds.minLat;
                body.maxLat = bounds.maxLat;
                body.minLng = bounds.minLng;
                body.maxLng = bounds.maxLng;
            }

                        const res = await fetch(`${API_BASE}/api/sel-heatmap-csv`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-User-Role": effectiveUserRole
                },
                body: JSON.stringify(body),
                signal: controller.signal
            });

            const payload = await res.json();

            if (!res.ok) {
                throw new Error(getFriendlySelErrorMessage(payload));
            }

            setSelHeatmapGeoJSON(payload);
            return payload;

               } catch (e) {
            if (e.name !== "AbortError") {
                console.error(e);
                alert(e.message || "Error retrieving CSV SEL heatmap.");
            }
            return null;
        } finally {
            setIsLoading(false);
            fetchController.current = null;
        }
    };

async function handleGenerateSelWithBounds(bounds) {

        setLastSelBounds(bounds);

        if (!fromDate || !toDate) {
            alert("Please select date range first!");
            return;
        }

        const geo = await fetchSelHeatmap(bounds);

        if (!geo) return;

        setSelHeatmapGeoJSON(geo);
    }



    const handleCancelAll = () => {
        if (fetchController.current) {
            fetchController.current.abort();
            fetchController.current = null;
        }
        if (csvParserRef.current) {
            csvParserRef.current.abort();
            csvParserRef.current = null;
        }
        setIsExporting(false);

        setIsLoading(false);
        setSelHeatmapGeoJSON(null);
    };




    const fixedActiveDays = (() => {
        const set = new Set();

        const start1 = new Date(Date.UTC(2025, 5, 21));
        const end1 = new Date(Date.UTC(2025, 8, 19));
        for (let d = new Date(start1); d <= end1; d.setUTCDate(d.getUTCDate() + 1)) {
            const y = d.getUTCFullYear();
            const m = String(d.getUTCMonth() + 1).padStart(2, "0");
            const day = String(d.getUTCDate()).padStart(2, "0");
            set.add(`${y}-${m}-${day}`);
        }

        const start2 = new Date(Date.UTC(2025, 10, 7));
        const end2 = new Date(Date.UTC(2025, 11, 5));


        for (let d = new Date(start2); d <= end2; d.setUTCDate(d.getUTCDate() + 1)) {
            const y = d.getUTCFullYear();
            const m = String(d.getUTCMonth() + 1).padStart(2, "0");
            const day = String(d.getUTCDate()).padStart(2, "0");
            set.add(`${y}-${m}-${day}`);
        }
        const start3 = new Date(Date.UTC(2025, 11, 15));
        const today = new Date();

        const end3 = new Date(Date.UTC(
            today.getUTCFullYear(),
            today.getUTCMonth(),
            today.getUTCDate()
        ));

        for (let d = new Date(start3); d <= end3; d.setUTCDate(d.getUTCDate() + 1)) {
            const y = d.getUTCFullYear();
            const m = String(d.getUTCMonth() + 1).padStart(2, "0");
            const day = String(d.getUTCDate()).padStart(2, "0");
            set.add(`${y}-${m}-${day}`);
        }
        set.add("2025-11-05");
        set.delete("2025-11-14");
        set.delete("2025-11-15");
        set.delete("2025-11-16");
        set.delete("2025-11-17");

        return set;
    })();




    const [availableDaysSet, setAvailableDaysSet] = useState(new Set(fixedActiveDays));

    function getBandIntensity(band) {
        if (band === "63Hz") return 0.7;
        if (band === "125Hz") return 0.8;
        if (band === "Broadband") return 0.95;
        return 1.0;
    }


    function monthKeyFromDate(date) {
        return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
    }


    useEffect(() => {
        const interval = setInterval(() => {
            const today = new Date();
            const key = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, "0")}-${String(today.getUTCDate()).padStart(2, "0")}`;
            setAvailableDaysSet(prev => new Set([...prev, key]));
        }, 6 * 60 * 60 * 1000);

        return () => clearInterval(interval);
    }, []);



    const normalizeRow = (row, headers) => {
        const h = {};
        headers.forEach(k => { h[k.toLowerCase()] = k; });

        const getVal = (...possible) => {
            for (const name of possible) {
                if (h[name.toLowerCase()] !== undefined && row[h[name.toLowerCase()]] !== undefined) {
                    return row[h[name.toLowerCase()]];
                }
            }
            return undefined;
        };

        return {
            MMSI: getVal("mmsi", "MMSI"),
            NAME: getVal("name", "vessel_name", "NAME"),
            LATITUDE: Number(getVal("lat", "latitude", "LATITUDE")),
            LONGITUDE: Number(getVal("lon", "lng", "longitude", "LONGITUDE")),
            SPEED: Number(getVal("speed", "sog", "SPEED")),
            NAVSTAT: Number(getVal("navstat", "NAVSTAT", "nav_status")),
            AISTYPE: Number(getVal("type", "aistype", "AISTYPE", "ship_type")),
            A: Number(getVal("a", "A")),
            B: Number(getVal("b", "B")),
            COURSE: Number(getVal("course", "COG", "COURSE")),
            HEADING: Number(getVal("heading", "HEADING")),
            timestamp: getVal("timestamp", "time_utc", "date time (utc)", "datetime", "vrijeme"),
        };
    };

    function handleExportSelWithBounds(bounds) {


        const margin = 0.05;
        const expanded = {
            minLat: bounds.minLat - margin,
            maxLat: bounds.maxLat + margin,
            minLng: bounds.minLng - margin,
            maxLng: bounds.maxLng + margin,
        };

        if (!selHeatmapGeoJSON) return alert("No SEL data within the selected area!");

        const rows = selHeatmapGeoJSON.features
            .filter(f => {
                let lat = f.geometry?.coordinates?.[1];
                let lon = f.geometry?.coordinates?.[0];

                if (!isFinite(lat) || !isFinite(lon)) {
                    lat = f.properties?.lat;
                    lon = f.properties?.lon;
                }

                return (
                    lat >= expanded.minLat &&
                    lat <= expanded.maxLat &&
                    lon >= expanded.minLng &&
                    lon <= expanded.maxLng
                );
            })
            .map(f => {
                let lat = f.geometry?.coordinates?.[1] ?? f.properties?.lat;
                let lon = f.geometry?.coordinates?.[0] ?? f.properties?.lon;
                return {
                    lat,
                    lon,
                    sel: f.properties.sel?.toFixed(2)
                };
            });


        if (rows.length === 0) {
            alert("No SEL data within the selected area!");
            return;
        }

        const csv = Papa.unparse(rows);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "SEL_export_area.csv";
        link.click();
    }


    function haversineDistance(lat1, lon1, lat2, lon2) {
        const toRad = (x) => (x * Math.PI) / 180;
        const R = 6371000;
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(lat1)) *
            Math.cos(toRad(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    const selectBoxStyle = {
        width: "100%",
        padding: "10px 12px",
        borderRadius: "8px",
        border: "1px solid #cbd5e1",
        background: "#fff",
        fontSize: "14px",
        color: "#222",
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        outline: "none",
        transition: "border-color .2s ease, box-shadow .2s ease, background-color .2s ease",
        cursor: "pointer",
    };

    const handleCalculate = () => {
        const lat = parseFloat(calcLat);
        const lng = parseFloat(calcLng);
        const userDepth = parseFloat(calcDepth);
        const userDepthSigned = -Math.abs(userDepth);

        if (actualDepth !== null) {
            if (userDepthSigned < actualDepth) {
                setDepthError(
                    `Entered depth (${userDepthSigned} m) is shallower than the actual depth (${actualDepth.toFixed(2)} m)`
                );
                return;
            } else {
                setDepthError("");
            }
        }

        const DURATION_SEC = 60;

        if (!lat || !lng) {
            alert("Enter coordinates.");
            return;
        }


        if (shipData && shipData.length > 0) {
            const selBlocks = [];

            for (const ship of shipData) {
                if (!ship.LATITUDE || !ship.LONGITUDE) continue;

                const horizontalDist = haversineDistance(
                    ship.LATITUDE, ship.LONGITUDE, lat, lng
                );

                if (horizontalDist > 150000) continue;

                const shipDepth = 0;
                const dist3D = Math.sqrt(
                    horizontalDist ** 2 + (userDepthSigned - shipDepth) ** 2
                );

                const sourceSpl = calculateNoiseByModel(ship, selectedModel, selectedBand);
                const spreading = 20 * Math.log10(dist3D);
                const absorption = 0.0005 * dist3D;
                const depthPenalty = Math.abs(userDepthSigned) * 0.1;

                const splAtPoint = sourceSpl - spreading - absorption - depthPenalty;

                const selBlock = splAtPoint + 10 * Math.log10(DURATION_SEC);
                selBlocks.push(selBlock);
            }

            if (selBlocks.length === 0) {
                setCalcResult({ sel: null, durationSec: DURATION_SEC });
                return;
            }

            const total = selBlocks.reduce((a, b) => a + Math.pow(10, b / 10), 0);
            const sel = 10 * Math.log10(total);

            setCalcResult({ sel, durationSec: DURATION_SEC });
            return;
        }


        if (!selHeatmapGeoJSON || selHeatmapGeoJSON.features.length === 0) {
            alert("No SEL heatmap generated.");
            return;
        }

        let nearest = null;
        let minDist = Infinity;

        selHeatmapGeoJSON.features.forEach(f => {
            const [lon, la] = f.geometry.coordinates;
            const d = haversineDistance(la, lon, lat, lng);
            if (d < minDist) {
                minDist = d;
                nearest = f;
            }
        });

        if (!nearest) {
            setCalcResult({ sel: null, durationSec: DURATION_SEC });
            return;
        }

        setCalcResult({
            sel: nearest.properties.sel,
            durationSec: DURATION_SEC
        });
    };




    function getVesselLength(ship) {
        const A = Number(ship.A) || 0;
        const B = Number(ship.B) || 0;
        const ab = A + B;
        const loa =
            Number(ship.LENGTH) ||
            Number(ship.length) ||
            Number(ship.LOA) ||
            Number(ship.loa) || 0;
        const L = loa > 0 ? loa : ab;
        return Math.max(L, 10);
    }

    function handleGenerateCsvSelClick() {

        setPendingSelExport(false);
        setPendingSelGeneration(false);
        setPendingSelCsvGeneration(true);

        setIsSelectingArea(true);
        setShowSelectionModal(true);

        const map = mapRef.current?.getMap?.();
        if (map) map.getCanvas().style.cursor = "crosshair";
    }



    const handleRegister = async (e) => {
        e.preventDefault();

        if (regPassword !== regPasswordConfirm) {
            setRegError("Passwords do not match.");
            return;
        }

        const strongPassword =
            /[A-Z]/.test(regPassword) &&
            /[0-9]/.test(regPassword) &&
            regPassword.length >= 8;

        if (!strongPassword) {
            setRegError("Password must be at least 8 characters long<br/>and include one uppercase letter and one number.");
            return;
        }


        if (!captchaToken) {
            setRegError("Please complete the captcha first.");
            return;
        }

        setRegError("");

        try {
            const res = await fetch(`${API_BASE}/api/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: regName,
                    surname: regSurname,
                    email: regEmail,
                    password: regPassword,
                    affiliation: regAffiliation,
                    intended_use: regIntendedUse,
                    role: regMakeAdmin ? "admin" : "user",
                    captcha: captchaToken

                })
            });

            const data = await res.json();

            if (!data.success) {
                setRegError(data.error || "Registration failed");
                return;
            }

            if (data.success) {
                setShowRegisterSuccess(true);
            }


            if (openedFromAdmin) {
                setShowLoginModal(false);
                setOpenedFromAdmin(false);
                return;
            }

            setAuthMode("login");
            setLoginEmail(regEmail);
            setLoginPassword("");

        } catch (err) {
            setRegError("Server error");
        }
    };

    function diffInMonths(date1, date2) {
        const years = date2.getFullYear() - date1.getFullYear();
        const months = date2.getMonth() - date1.getMonth();
        return years * 12 + months;
    }

    function diffInDays(date1, date2) {
        const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
        const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());

        const diffMs = d2 - d1;
        return diffMs / (1000 * 60 * 60 * 24);
    }


    function validateDateRange(start, end, user, resetDates) {
        if (!start || !end) return true;

        if (!user) {
            if (diffInDays(start, end) > 0) {
                alert("Guest users can only select a 1 day range.");
                resetDates();
                return false;
            }
        }

        if (user?.role === "user") {
            if (diffInMonths(start, end) > 1) {
                alert("As a registered user, you can select a maximum range of 1 month.");
                resetDates();
                return false;
            }
        }

        return true;
    }



    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError("");

        try {
            const res = await fetch(`${API_BASE}/api/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: loginEmail,
                    password: loginPassword
                })
            });

            const data = await res.json();

            if (!data.success) {
                setLoginError(data.error || "Incorrect email or password");
                return;
            }

            sessionStorage.setItem("jwtToken", data.token);
            sessionStorage.setItem("user", JSON.stringify(data.user));


            setIsLoggedIn(true);


            setShowLoginModal(false);
            setLoginEmail("");
            setLoginPassword("");
        } catch (err) {
            setLoginError("Server error");
        }
    };

    const user = JSON.parse(sessionStorage.getItem("user"));

const effectiveUserRole =
    user?.role === "admin" ? "admin" :
    user?.role === "user" ? "user" :
    "guest";



    function handleExportSelClick() {
        setPendingSelExport(true);
        setPendingSelGeneration(false);
        setPendingSelCsvGeneration(false);

        setShowSelectionModal(true);
        setIsSelectingArea(true);

        const map = mapRef.current?.getMap?.();
        if (map) map.getCanvas().style.cursor = "crosshair";
    }



    const db2lin = (L_dB) => Math.pow(10, L_dB / 10);
    const lin2db = (P_lin) => 10 * Math.log10(Math.max(P_lin, 1e-20));

    const K6 = Math.pow(2, 1 / 6);
    const K12 = Math.pow(2, 1 / 12);

    function thirdOctNodes(fc) {
        return [fc / K6, fc / K12, fc, fc * K12, fc * K6];
    }

    function booleWeightsLogF(fa, fb) {
        const h = (Math.log(fb) - Math.log(fa)) / 4;
        const base = [7, 32, 12, 32, 7];
        return base.map((c) => c * (2 * h) / 45);
    }

    function thirdOctBandLevel_fromPSD(psdFive_dB, fc) {
        const fNodes = thirdOctNodes(fc);
        const weights = booleWeightsLogF(fNodes[0], fNodes[4]);

        let sum = 0;
        for (let i = 0; i < 5; i++) {
            sum += weights[i] * fNodes[i] * db2lin(psdFive_dB[i]);
        }
        return lin2db(sum);
    }
    function randi_psd_perHz(f, v_knots, length_m) {
        const logf = Math.log10(f);
        const length_ft = length_m * 3.28084;

        let SL0;
        if (f <= 500) {
            const t1 = Math.pow(10, (-1.06 * logf - 14.34));
            const t2 = Math.pow(10, (3.32 * logf - 21.425));
            SL0 = -10 * Math.log10(t1 + t2);
        } else {
            SL0 = 173.2 - 18 * logf;
        }

        const df = (f <= 28.4) ? 8.1 : (22.3 - 9.77 * logf);

        const dL = Math.pow(length_ft, 1.15) / 3643.0;

        const SL = SL0
            + 60 * Math.log10(v_knots / 12)
            + 20 * Math.log10(length_ft / 300)
            + df * dL
            + 3;

        return SL;
    }


    function booleBandLevel_log(fc, v_knots, length_m) {
        const k = Math.pow(2, 1 / 6);
        let f_lo = fc / k;
        let f_hi = fc * k;

        const log_lo = Math.log(f_lo);
        const log_hi = Math.log(f_hi);

        const h = (log_hi - log_lo) / 4;

        const w = [7, 32, 12, 32, 7];
        let sum = 0;

        for (let i = 0; i < 5; i++) {
            const log_fi = log_lo + h * i;
            const fi = Math.exp(log_fi);

            const SL_psd = randi_psd_perHz(fi, v_knots, length_m);
            const p_lin = Math.pow(10, SL_psd / 10);

            sum += w[i] * p_lin * fi;
        }

        const integral = (2 * h / 45) * sum;
        return 10 * Math.log10(integral);
    }

    function broadbandRANDI(v_knots, length_m) {
        const centers = [
            20, 25, 31.5, 40, 50, 63, 80, 100,
            125, 160, 200, 250, 315, 400, 500,
            630, 800, 1000, 1250, 1600, 2000
        ];

        let E = 0;

        for (const fc of centers) {
            const Lb = booleBandLevel_log(fc, v_knots, length_m);
            E += Math.pow(10, Lb / 10);
        }

        return 10 * Math.log10(E);
    }

    function broadband20_2000_viaThirdOct(psdFunc) {
        const centers = [
            20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160,
            200, 250, 315, 400, 500, 630, 800, 1000, 1250,
            1600, 2000
        ];

        let Psum = 0;
        centers.forEach((fc) => {
            const fNodes = thirdOctNodes(fc);
            const psd5 = fNodes.map((f) => psdFunc(f));
            const bandLevel = thirdOctBandLevel_fromPSD(psd5, fc);
            Psum += db2lin(bandLevel);
        });

        return lin2db(Psum);
    }

    function getShipCategory(type) {
        if (type >= 60 && type <= 69) return "Passenger";
        if (type >= 70 && type <= 79) return "Cargo";
        if (type >= 80 && type <= 89) return "Tankers";
        if (type === 30) return "Fishing";
        if (type === 36) return "Sailing";
        if (type === 37) return "Pleasure";
        return "Unknown";
    }

    function isVesselSupported(ship, model) {
        const category = getShipCategory(ship.AISTYPE);

        const supported = {
            RANDI: ["Passenger", "Cargo", "Tankers"],
            JOMOPANS: ["Passenger", "Cargo", "Tankers", "Fishing"],
            LBDS: ["Passenger", "Cargo", "Tankers"],
            AQUO: ["Passenger", "Cargo", "Tankers", "Sailing", "Pleasure", "Fishing"],
            AVERAGE: ["Passenger", "Cargo", "Tankers", "Sailing", "Pleasure", "Fishing"]
        };

        return supported[model]?.includes(category) ?? false;
    }


    function normalizeClass(c) {
        c = c.toLowerCase();
        if (c.includes("tanker")) return "tanker";
        if (c.includes("cargo") || c.includes("container") || c.includes("bulker")) return "cargo";
        if (c.includes("passenger")) return "passenger";
        if (c.includes("fishing")) return "fishing";
        return "other";
    }

    function lsSpectralDensity_JECHO(f, vesselClass, speedKnots, lengthM) {
        const L0 = 91.44;
        const K = 191.0;
        const K_LF = 208.0;

        const VC = {
            fishing: 6.4,
            cargo: 13.9,
            tanker: 12.4,
            passenger: 9.7,
            other: 7.4
        };

        const DLF = { cargo: 0.8, tanker: 1.0 };

        vesselClass = normalizeClass(vesselClass);
        const vc = VC[vesselClass] || 7.4;

        const fhat = f / 1.0;
        const fhat1 = 480 * (1 / vc);
        const fhat1_LF = 600 * (1 / vc);

        let LS0;

        if ((vesselClass === "cargo" || vesselClass === "tanker") && f < 100) {
            const dlf = DLF[vesselClass];
            const inner = 1 - (fhat / fhat1) ** 2;
            const denom = inner ** 2 + dlf ** 2;
            LS0 = K_LF - 40 * Math.log10(fhat1_LF) + 10 * Math.log10(fhat) - 10 * Math.log10(denom);
        } else {
            const D = 3.0;
            const inner = 1 - (fhat / fhat1);
            const denom = inner ** 2 + D ** 2;
            LS0 = K - 20 * Math.log10(fhat1) - 10 * Math.log10(denom);
        }

        const speedTerm = 60 * Math.log10(speedKnots / vc);
        const lengthTerm = 20 * Math.log10(lengthM / L0);

        return LS0 + speedTerm + lengthTerm;
    }

    function booleIntegrate(func, a, b) {
        const h = (b - a) / 4;
        const f0 = func(a);
        const f1 = func(a + h);
        const f2 = func(a + 2 * h);
        const f3 = func(a + 3 * h);
        const f4 = func(b);

        return (2 * h / 45) * (7 * f0 + 32 * f1 + 12 * f2 + 32 * f3 + 7 * f4);
    }

    function thirdOctBandEdges(fc) {
        const ratio = Math.pow(10, 0.05);
        return [fc / ratio, fc * ratio];
    }

    function jomopansBand(fc, ship) {
        const [f1, f2] = thirdOctBandEdges(fc);

        const psd = (f) => {
            const L = lsSpectralDensity_JECHO(
                f,
                getShipCategory(ship.AISTYPE),
                Math.max(ship.SPEED, 0.1),
                getVesselLength(ship)
            );
            return Math.pow(10, L / 10);
        };

        const power = booleIntegrate(psd, f1, f2);
        return 10 * Math.log10(power);
    }


    function jomopansBroadband(ship) {
        const centers = [
            20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160,
            200, 250, 315, 400, 500, 630, 800, 1000, 1250,
            1600, 2000
        ];

        let sum = 0;
        for (const fc of centers) {
            const L = jomopansBand(fc, ship);
            sum += Math.pow(10, L / 10);
        }
        return 10 * Math.log10(sum);
    }



    function log10(x) {
        return Math.log(x) / Math.LN10;
    }

    function lin(x) {
        return Math.pow(10, x / 10);
    }

    function dB(x) {
        return 10 * Math.log10(x);
    }


    function thirdOctNodes2(fc) {
        const ratio = Math.pow(2, 1 / 6);
        const f1 = fc / ratio;
        const f2 = fc * ratio;

        const h = (Math.log(f2) - Math.log(f1)) / 4;

        return [
            Math.exp(Math.log(f1)),
            Math.exp(Math.log(f1) + h),
            Math.exp(Math.log(f1) + 2 * h),
            Math.exp(Math.log(f1) + 3 * h),
            Math.exp(Math.log(f2))
        ];
    }



    function booleWeights() {
        return [7, 32, 12, 32, 7];
    }

    function getAQUOparams(ship) {
        const cat = getShipCategory(ship.AISTYPE);

        if (cat === "Cargo" || cat === "Tankers") {
            return {
                Lref: 180,
                Vcav: 10
            };
        }

        if (cat === "Passenger") {
            return {
                Lref: 150,
                Vcav: 12
            };
        }

        if (cat === "Fishing") {
            return {
                Lref: 80,
                Vcav: 8
            };
        }

        return {
            Lref: 180,
            Vcav: 10
        };
    }


    function SL_mach(f, V) {
        if (f < 200) {
            return 136 + 15 * log10(V);
        }
        return 186 - 22 * log10(f) + 15 * log10(V);
    }

    function SL_prop(f, V) {
        if (f < 80) {
            return 109 - 51 * log10(f) + 50 * log10(V);
        }
        return 156 - 30 * log10(f) + 50 * log10(V);
    }

    function SL_cav(f, V) {
        if (V <= 10) return null;

        if (f < 50) {
            return 79 + 10 * log10(f) + 60 * log10(V);
        }
        return 129 - 20 * log10(f) + 60 * log10(V);
    }

    function SL_total(f, V, L, params) {
        const { Lref, Vcav } = params;

        const mach = SL_mach(f, V);
        const prop = SL_prop(f, V);

        let sum = lin(mach) + lin(prop);

        if (V > Vcav) {
            const cav = SL_cav(f, V);
            sum += lin(cav);
        }

        return dB(sum) + 25 * log10(L / Lref);
    }


    function AQUO_band(fc, V, L, ship) {
        const nodes = thirdOctNodes2(fc);
        const weights = booleWeights();
        const params = getAQUOparams(ship);

        let sum = 0;
        for (let i = 0; i < 5; i++) {
            const SL = SL_total(nodes[i], V, L, params);
            sum += weights[i] * lin(SL);
        }

        const df_log = (Math.log(nodes[4]) - Math.log(nodes[0])) / 4;
        return dB((2 * df_log / 45) * sum);
    }



    function AQUO_broadband(V, L, ship) {
        const centers = [
            20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160,
            200, 250, 315, 400, 500, 630, 800, 1000, 1250,
            1600, 2000
        ];

        let sum = 0;
        for (const fc of centers) {
            const Lband = AQUO_band(fc, V, L, ship);
            sum += lin(Lband);
        }

        return dB(sum);
    }


    function AQUO_sailboat(band) {
        if (band === "63Hz") return 120;
        if (band === "125Hz") return 120;
        if (band === "Broadband") return 133;
        return 120;
    }


    function SRV_model(V, L) {
        const b0 = 100.3;
        const bL = 19.1 * log10(L);
        const bV = 27.8 * log10(V);
        const bLV = (-3.1) * log10(L * V);

        return b0 + bL + bV + bLV;
    }





    const log10_model = x => Math.log10(x);
    const lin_model = x => Math.pow(10, x / 10);
    const dB_model = x => 10 * Math.log10(x);


    function getVesselLengthFrontend(ship) {
        const A = Number(ship.A) || 0;
        const B = Number(ship.B) || 0;
        return Math.max(A + B, 10);
    }

    function randiBandFrontend(fc, speedKnots, lengthM) {
        const K6 = Math.pow(2, 1 / 6);
        const nodes = [
            fc / K6,
            fc / Math.pow(2, 1 / 12),
            fc,
            fc * Math.pow(2, 1 / 12),
            fc * K6
        ];

        function psd(f) {
            const logf = log10(f);
            const length_ft = lengthM * 3.28084;

            let SL0;
            if (f <= 500) {
                const t1 = Math.pow(10, (-1.06 * logf - 14.34));
                const t2 = Math.pow(10, (3.32 * logf - 21.425));
                SL0 = -10 * log10(t1 + t2);
            } else {
                SL0 = 173.2 - 18 * logf;
            }

            const df = (f <= 28.4) ? 8.1 : (22.3 - 9.77 * logf);
            const dL = Math.pow(length_ft, 1.15) / 3643.0;

            return SL0
                + 60 * log10(speedKnots / 12)
                + 20 * log10(length_ft / 300)
                + df * dL + 3;
        }

        const w = [7, 32, 12, 32, 7];
        let sum = 0;

        for (let i = 0; i < 5; i++) {
            sum += w[i] * lin(psd(nodes[i])) * nodes[i];
        }

        const h = (Math.log(nodes[4]) - Math.log(nodes[0])) / 4;
        return dB((2 * h / 45) * sum);
    }

    function randiBroadbandFrontend(speed, length) {
        const bands = [
            20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160,
            200, 250, 315, 400, 500, 630, 800, 1000,
            1250, 1600, 2000
        ];

        let P = 0;
        bands.forEach(fc => P += lin(randiBandFrontend(fc, speed, length)));
        return dB(P);
    }

    function jomoBandFrontend(fc, ship) {
        return 150 + 3 * log10(ship.speed + 1);
    }

    function jomoBroadbandFrontend(ship) {
        return 150 + 3 * log10(ship.speed + 1);
    }

    const aquoBroadbandFrontend = ship =>
        120 + 10 * log10(ship.speed + 1);

    const lbdsBroadbandFrontend = ship =>
        115 + 8 * log10(ship.speed + 1);

    function averageModelFrontend(ship, band) {
        const values = [];

        if (band !== "Broadband") {
            const fc = Number(band.replace("Hz", ""));

            values.push(randiBandFrontend(fc, ship.speed, ship.length));
            values.push(jomoBandFrontend(fc, ship));
        } else {
            values.push(randiBroadbandFrontend(ship.speed, ship.length));
            values.push(jomoBroadbandFrontend(ship));
            values.push(aquoBroadbandFrontend(ship));
            values.push(lbdsBroadbandFrontend(ship));
        }

        const valid = values.filter(v => isFinite(v));
        if (!valid.length) return null;

        return dB(
            valid.reduce((sum, L) => sum + lin(L), 0) / valid.length
        );
    }

    function calculateNoiseByModel(ship, model, band) {

        if (!["63Hz", "125Hz", "Broadband"].includes(band)) {
            return null;
        }

        const row = {
            speed: Number(ship.SPEED),
            a: ship.A,
            b: ship.B,
            aistype: ship.AISTYPE
        };

        const frontendShip = {
            speed: Number(row.speed),
            length: getVesselLengthFrontend(row)
        };

        return averageModelFrontend(frontendShip, band);
    }



    function generateSelHeatmapFromCsv(data, gridSize = 0.02) {

        const grid = {};

        for (const row of data) {

            if (
                typeof row.LATITUDE !== "number" || isNaN(row.LATITUDE) ||
                typeof row.LONGITUDE !== "number" || isNaN(row.LONGITUDE) ||
                typeof row.SPEED !== "number" || isNaN(row.SPEED) ||
                typeof row.A !== "number" || isNaN(row.A) ||
                typeof row.B !== "number" || isNaN(row.B)
            ) continue;

            if (row.SPEED <= 0) continue;

            if (![0, 2, 3, 4, 7].includes(row.NAVSTAT)) continue;

            const lat = Math.round(row.LATITUDE / gridSize) * gridSize;
            const lon = Math.round(row.LONGITUDE / gridSize) * gridSize;
            const key = `${lat.toFixed(3)},${lon.toFixed(3)}`;

            const ship = {
                SPEED: row.SPEED,
                AISTYPE: row.AISTYPE,
                A: row.A,
                B: row.B
            };

            const sourceSpl = calculateNoiseByModel(ship, "AVERAGE", selectedBand);


            if (!isFinite(sourceSpl)) continue;

            const sel = sourceSpl + 10 * Math.log10(1);
            const energy = Math.pow(10, sel / 10);

            if (!grid[key]) grid[key] = [];
            grid[key].push(energy);
        }

        const features = [];

        for (const [key, values] of Object.entries(grid)) {
            const [lat, lon] = key.split(",").map(Number);

            const totalEnergy = values.reduce((a, b) => a + b, 0);
            const sel = 10 * Math.log10(totalEnergy);

            features.push({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [lon, lat]
                },
                properties: { sel }
            });
        }

        if (features.length > 0) {
            const all = features.map(f => f.properties.sel);
            const min = Math.min(...all);
            const max = Math.max(...all);

            features.forEach(f => {
                f.properties.sel_normalized =
                    max > min ? (f.properties.sel - min) / (max - min) : 0;
            });
        }

        return {
            type: "FeatureCollection",
            features
        };
    }




    useEffect(() => {
        let interval;
        if (isPlaying) {
            interval = setInterval(() => {
                setCurrentTime((prevTime) => new Date(prevTime.getTime() + 60000));
            }, playSpeed);
        } else {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [isPlaying, playSpeed]);

    useEffect(() => {
        if (activeTab === "controls1") {
            setCsvData(null);
        }
        if (activeTab === "controls2") {
            setShipData([]);
        }

        setFromDate(null);
        setToDate(null);
        setSelHeatmapGeoJSON(null);
        setSelectedShip(null);
    }, [activeTab]);




    useEffect(() => {
        setAvailableDaysSet(prev => new Set([...prev, ...fixedActiveDays]));
    }, []);



    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== "Escape") return;

            const map = mapRef.current?.getMap?.();

            setShowSelectionModal(false);
            setIsPickingLocation(false);
            setIsSelectingArea(false);
            setSelectionStart(null);
            setSelectionEnd(null);
            setSelectionCoords(null);

            if (pickHintRef.current)
                pickHintRef.current.style.display = "none";

            if (map)
                map.getCanvas().style.cursor = "";
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);



    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map) return;

        const onData = (e) => {
            if (!showMPA) return;
            if (e.sourceId === "mpa" && e.isSourceLoaded) {
                console.log("MPA data loaded.");
                setMpaLoading(false);
            }
        };

        map.on("sourcedata", onData);

        return () => map.off("sourcedata", onData);

    }, [showMPA]);


    useEffect(() => {
        if (isSelectingArea) return;
        if (!selectionCoords) return;

        const map = mapRef.current?.getMap?.();

        if (pendingSelGeneration) {
            handleGenerateSelWithBounds(selectionCoords);
            setPendingSelGeneration(false);
        }
        else if (pendingSelExport) {
            handleExportSelWithBounds(selectionCoords);
            setPendingSelExport(false);
        }

        setSelectionCoords(null);

        if (map) map.getCanvas().style.cursor = "";

    }, [isSelectingArea, selectionCoords, pendingSelGeneration, pendingSelExport]);



    useEffect(() => {
        document.title = "Sound exposure level";
    }, []);


    useEffect(() => {
        if (showLoginModal && authMode === "register") {
            setTimeout(() => {
                if (window.grecaptcha && document.getElementById("recaptcha-register")) {
                    window.grecaptcha.render("recaptcha-register", {
                        sitekey: "6LcWBh4sAAAAAFk5yuvXn7hkE0wV-uAmh1dQi2Xz",
                        callback: (token) => setCaptchaToken(token)
                    });
                }
            }, 50);
        }
    }, [showLoginModal, authMode]);


    useEffect(() => {
        if (showLoginModal) {
            if (authMode === "register") {
                setRegName("");
                setRegSurname("");
                setRegEmail("");
                setRegPassword("");
                setRegPasswordConfirm("");
                setRegAffiliation("");
                setRegIntendedUse("Academic use");
                setCaptchaToken("");
                setRegError("");
            }

            if (authMode === "login") {
                setLoginEmail("");
                setLoginPassword("");
                setLoginError("");
            }
        }
    }, [showLoginModal, authMode]);


    useEffect(() => {
        const handler = (e) => {
            setCaptchaToken(e.detail);
        };

        window.addEventListener("captcha-done", handler);

        return () => window.removeEventListener("captcha-done", handler);
    }, []);


    useEffect(() => {
        if (!showLoginModal) {
            setShowPassword(false);
            setShowPasswordConfirm(false);
            setShowLoginPassword(false);
        }
    }, [showLoginModal]);

    useEffect(() => {
        setShowPassword(false);
        setShowPasswordConfirm(false);
        setShowLoginPassword(false);
    }, [authMode, openedFromAdmin]);


    useEffect(() => {
        if (!lastSelBounds) return;
        if (!fromDate || !toDate) return;

        console.log("🔄 Noise type changed → regenerating SEL");

        handleGenerateSelWithBounds(lastSelBounds);
    }, [selectedModel, selectedBand]);

useEffect(() => {
    if (!csvData || csvData.length === 0) return;
    if (!lastSelBounds) return;

    console.log("🔄 Recomputing CSV SEL with new noise type");

    const sourceBounds = expandBoundsByMeters(
        lastSelBounds,
        getSelMaxRangeMeters(selectedBand)
    );

    const filteredCsv = csvData.filter(row => {
        const lat = Number(row.LATITUDE);
        const lon = Number(row.LONGITUDE);

        const inBounds =
            lat >= sourceBounds.minLat &&
            lat <= sourceBounds.maxLat &&
            lon >= sourceBounds.minLng &&
            lon <= sourceBounds.maxLng;

        const inDates = isRowWithinSelectedDates(row, fromDate, toDate);

        return inBounds && inDates;
    });

    fetchSelHeatmapFromCsv(filteredCsv, lastSelBounds);
}, [selectedModel, selectedBand, fromDate, toDate, csvData, lastSelBounds]);

    useEffect(() => {
        if (!selHeatmapGeoJSON || !selHeatmapGeoJSON.features?.length) {
            setSelMin(null);
            setSelMax(null);
            return;
        }

        const values = selHeatmapGeoJSON.features
            .map(f => f.properties?.sel)
            .filter(v => typeof v === "number" && isFinite(v));

        if (values.length === 0) {
            setSelMin(null);
            setSelMax(null);
            return;
        }

        setSelMin(Math.min(...values));
        setSelMax(Math.max(...values));
    }, [selHeatmapGeoJSON]);


    function toUTCDateKey(date) {
        const y = date.getUTCFullYear();
        const m = String(date.getUTCMonth() + 1).padStart(2, "0");
        const d = String(date.getUTCDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }

    function parseRowTimestamp(row) {
    const raw = row?.timestamp;
    if (!raw) return null;

    const normalized = String(raw).includes("T")
        ? String(raw)
        : String(raw).replace(" ", "T");

    const withZone = /Z$|[+-]\d{2}:\d{2}$/.test(normalized)
        ? normalized
        : `${normalized}Z`;

    const d = new Date(withZone);
    return isNaN(d.getTime()) ? null : d;
}

function isRowWithinSelectedDates(row, fromDate, toDate) {
    if (!fromDate || !toDate) return true;

    const ts = parseRowTimestamp(row);
    if (!ts) return false;

    const from = new Date(fromDate);
    from.setHours(0, 0, 0, 0);

    const to = new Date(toDate);
    to.setHours(23, 59, 59, 999);

    return ts >= from && ts <= to;
}










    return (
        <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
            {showSelectionModal && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 2000
                }}>
                    <div style={{
                        backgroundColor: "white",
                        padding: "25px",
                        borderRadius: "12px",
                        width: "350px",
                        textAlign: "center",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.3)"



                    }}>
                        <h3 style={{ marginBottom: "15px" }}>Select area</h3>

                        <p style={{ fontSize: "15px", marginBottom: "20px" }}>
                            Click to set the starting point (1st click), then click again to finish the selection (2nd click).                 </p>
                        <p style={{ fontSize: "15px", marginBottom: "20px" }}>
                            Press ESC to cancel at any time.                        </p>

                        <button
                            onClick={() => setShowSelectionModal(false)}
                            style={{
                                padding: "8px 15px",
                                backgroundColor: "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontWeight: "bold"
                            }}
                        >
                            <h2 style={{ margin: 0, marginLeft: 8, marginRight: 8, textAlign: "center" }}>OK</h2>
                        </button>
                    </div>
                </div>
            )}

            {showLoginModal && (

                <div
                    style={{
                        position: "fixed",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(0,0,0,0.4)",
                        zIndex: 5000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                    onClick={closeLoginModal}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={authMode === "login" ? handleLogin : handleRegister}
                        style={{
                            background: "#fff",
                            padding: "35px 25px 25px 25px",
                            borderRadius: "14px",
                            boxShadow: "0 6px 24px rgba(0,0,0,0.21)",
                            minWidth: 365,
                            display: "flex",
                            flexDirection: "column",
                            gap: 12.5,
                            position: "relative"
                        }}
                    >

                        <div
                            onClick={closeLoginModal}
                            style={{
                                position: "absolute",
                                top: "10px",
                                right: "10px",
                                fontSize: "22px",
                                cursor: "pointer",
                                color: "#777",
                                fontWeight: "bold",
                                lineHeight: "1"
                            }}
                        >
                            ×
                        </div>


                        <h2 style={{ margin: 0, marginBottom: 12, textAlign: "center" }}>
                            {authMode === "login" ? "Login" : "Create account"}
                        </h2>

                        {authMode === "login" ? (

                            <>




                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={loginEmail}
                                    onChange={e => setLoginEmail(e.target.value)}
                                    required
                                    style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                                />
                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        type={showLoginPassword ? "text" : "password"}
                                        placeholder="Password"
                                        value={loginPassword}
                                        onChange={e => setLoginPassword(e.target.value)}
                                        required
                                        style={{
                                            padding: "8px 36px 8px 9px",
                                            fontSize: 16,
                                            borderRadius: 6,
                                            border: "1px solid #ccc",
                                            width: "100%",
                                            boxSizing: "border-box"
                                        }}
                                    />

                                    <img
                                        src={showLoginPassword ? "/hide.png" : "/show.png"}
                                        alt="toggle visibility"
                                        onClick={() => setShowLoginPassword(prev => !prev)}
                                        style={{
                                            position: "absolute",
                                            right: 10,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            width: 20,
                                            height: 20,
                                            cursor: "pointer",
                                            opacity: 0.7
                                        }}
                                    />
                                </div>


                                {loginError && (
                                    <div style={{ color: "red", textAlign: "center", fontSize: 14 }}>
                                        {loginError}
                                    </div>
                                )}

                                <button type="submit" style={{ ...buttonStyle, width: "100%" }}>
                                    Login
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setAuthMode("register")}
                                    style={{
                                        ...buttonStyle,
                                        backgroundColor: "#ccc",
                                        color: "#222",
                                        width: "100%"
                                    }}
                                >
                                    Create account
                                </button>
                            </>
                        ) : (
                            <>
                                {user && user.role === "admin" && (
                                    <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px" }}>
                                        <input
                                            type="checkbox"
                                            checked={regMakeAdmin}
                                            onChange={(e) => setRegMakeAdmin(e.target.checked)}
                                        />
                                        Make this user an administrator
                                    </label>
                                )}

                                <input
                                    type="text"
                                    placeholder="Name"
                                    value={regName}
                                    onChange={e => setRegName(e.target.value)}
                                    required
                                    style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                                />

                                <input
                                    type="text"
                                    placeholder="Surname"
                                    value={regSurname}
                                    onChange={e => setRegSurname(e.target.value)}
                                    required
                                    style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                                />

                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={regEmail}
                                    onChange={e => setRegEmail(e.target.value)}
                                    required
                                    style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                                />

                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Password"
                                        value={regPassword}
                                        onChange={e => setRegPassword(e.target.value)}
                                        required
                                        style={{
                                            padding: "8px 36px 8px 9px",
                                            fontSize: 16,
                                            borderRadius: 6,
                                            border: "1px solid #ccc",
                                            width: "100%",
                                            boxSizing: "border-box"
                                        }}
                                    />

                                    <img
                                        src={showPassword ? "/hide.png" : "/show.png"}
                                        alt="toggle visibility"
                                        onClick={() => setShowPassword(prev => !prev)}
                                        style={{
                                            position: "absolute",
                                            right: 10,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            width: 20,
                                            height: 20,
                                            cursor: "pointer",
                                            opacity: 0.7
                                        }}
                                    />
                                </div>

                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        type={showPasswordConfirm ? "text" : "password"}
                                        placeholder="Confirm password"
                                        value={regPasswordConfirm}
                                        onChange={e => setRegPasswordConfirm(e.target.value)}
                                        required
                                        style={{
                                            padding: "8px 36px 8px 9px",
                                            fontSize: 16,
                                            borderRadius: 6,
                                            border: "1px solid #ccc",
                                            width: "100%",
                                            boxSizing: "border-box"
                                        }}
                                    />

                                    <img
                                        src={showPasswordConfirm ? "/hide.png" : "/show.png"}
                                        alt="toggle visibility"
                                        onClick={() => setShowPasswordConfirm(prev => !prev)}
                                        style={{
                                            position: "absolute",
                                            right: 10,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            width: 20,
                                            height: 20,
                                            cursor: "pointer",
                                            opacity: 0.7
                                        }}
                                    />
                                </div>



                                <input
                                    type="text"
                                    placeholder="Affiliation (University / Institution)"
                                    value={regAffiliation}
                                    onChange={e => setRegAffiliation(e.target.value)}
                                    required
                                    style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                                />

                                <label style={{ marginTop: "5px", fontWeight: "bold" }}>Intended use:</label>

                                <select
                                    value={regIntendedUse}
                                    onChange={(e) => setRegIntendedUse(e.target.value)}
                                    style={{
                                        padding: 10,
                                        fontSize: 16,
                                        borderRadius: 6,
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                >
                                    <option value="Academic use">Academic use</option>
                                    <option value="Policy initiatives">Policy initiatives</option>
                                    <option value="Management of marine areas">Management of marine areas</option>
                                    <option value="General interest in marine environment">General interest in marine environment</option>
                                </select>

                                <div style={{ marginTop: "10px" }}>
                                    <div
                                        id="recaptcha-register"
                                        className="g-recaptcha"
                                        data-sitekey="6LcWBh4sAAAAAFk5yuvXn7hkE0wV-uAmh1dQi2Xz"
                                    ></div>

                                </div>

                                {regError && (
                                    <div
                                        style={{ color: "red", textAlign: "center" }}
                                        dangerouslySetInnerHTML={{ __html: regError }}
                                    ></div>
                                )}



                                <button type="submit" style={{ ...buttonStyle, width: "100%" }}>
                                    Register
                                </button>

                                {!openedFromAdmin && (
                                    <button
                                        type="button"
                                        onClick={() => setAuthMode("login")}
                                        style={{
                                            ...buttonStyle,
                                            backgroundColor: "#ccc",
                                            color: "#222",
                                            width: "100%"
                                        }}
                                    >
                                        Back to login
                                    </button>
                                )}

                            </>

                        )}
                    </form>
                </div>
            )}


            {showAdminPanel && (
                <div
                    style={{
                        position: "fixed",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(0,0,0,0.4)",
                        zIndex: 6000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                    onClick={() => setShowAdminPanel(false)}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            position: "relative",
                            background: "#fff",
                            padding: "25px",
                            borderRadius: "12px",
                            width: "100%",
                            maxWidth: "1000px",
                            maxHeight: "80vh",
                            overflowY: "scroll",
                            boxShadow: "0 5px 20px rgba(0,0,0,0.3)"
                        }}
                    >
                        <button
                            type="button"

                            onClick={() => setShowAdminPanel(false)}
                            style={{
                                position: "absolute",
                                top: "18px",
                                right: "18px",
                                background: "transparent",
                                border: "none",
                                fontSize: "26px",
                                fontWeight: "bold",
                                color: "#777",
                                cursor: "pointer",
                                zIndex: 10,
                                lineHeight: "1"
                            }}
                            aria-label="Close"
                        >
                            ×
                        </button>

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "25px"
                            }}
                        >
                            <button
                                onClick={() => {
                                    setOpenedFromAdmin(true);
                                    setAuthMode("register");
                                    setShowAdminPanel(false);
                                    setShowLoginModal(true);
                                }}
                                style={{
                                    backgroundColor: "#007bff",
                                    color: "white",
                                    padding: "10px 20px",
                                    borderRadius: "10px",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    boxShadow: "0 3px 8px rgba(0,0,0,0.2)"
                                }}
                            >
                                ➕ Create user account
                            </button>


                            <h2 style={{ flex: 1, textAlign: "center", margin: 0 }}>
                                👤 All registered users
                            </h2>

                            <div style={{ width: "160px" }}></div>
                        </div>


                        {adminError && (
                            <div style={{ color: "red", textAlign: "center" }}>{adminError}</div>
                        )}

                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: "#007bff", color: "white" }}>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Name</th>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Surname</th>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Email</th>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Affiliation</th>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Use</th>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Role</th>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {adminUsers.map(u => (
                                    <tr key={u.id} style={{ borderBottom: "1px solid #ccc" }}>
                                        <td style={{ textAlign: "left", padding: "8px" }}>{u.name}</td>
                                        <td style={{ textAlign: "left", padding: "8px" }}>{u.surname}</td>
                                        <td style={{ textAlign: "left", padding: "8px" }}>{u.email}</td>
                                        <td style={{ textAlign: "left", padding: "8px" }}>{u.affiliation}</td>
                                        <td style={{ textAlign: "left", padding: "8px" }}>{u.intended_use}</td>
                                        <td style={{ textAlign: "left", padding: "8px" }}>{u.role}</td>
                                        <td style={{ textAlign: "left", padding: "8px" }}>
                                            <button
                                                onClick={() => {
                                                    setDeleteUserId(u.id);
                                                    setShowDeleteModal(true);
                                                }}
                                                style={{
                                                    background: "red",
                                                    color: "white",
                                                    padding: "5px 10px",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                    border: "none"
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>

                        </table>
                    </div>
                </div>
            )}

            <button
                onClick={() => setIsSidebarOpen(true)}
                style={{
                    position: "absolute",
                    top: "20px",
                    left: "20px",
                    zIndex: 1200,
                    background: "#007bff",
                    color: "white",
                    border: "none",
                    padding: "10px 15px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "25px",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
                    display: isSidebarOpen ? "none" : "block"
                }}
            >
                ☰
            </button>

            {isSidebarOpen && (
                <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "250px",
                    bottom: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    boxShadow: "2px 0 10px rgba(0,0,0,0.2)",
                    zIndex: 1100,
                    display: "flex",
                    flexDirection: "column",
                    padding: "20px",
                    transition: "all 0.3s ease",
                    fontFamily: "Arial, sans-serif"
                }}>
                    <img
                        src={`${process.env.PUBLIC_URL}/SHIPECHO_LOGO.png`}
                        alt="SHIPECHO"
                        style={{
                            width: "120px",
                            display: "block",
                            margin: "-10px auto -32px auto",
                            borderRadius: "12px"
                        }}
                    />

                    <button
                        onClick={() => setIsSidebarOpen(false)}
                        style={{
                            position: "absolute",
                            top: "18px",
                            right: "18px",
                            background: "transparent",
                            border: "none",
                            fontSize: "26px",
                            fontWeight: "bold",
                            color: "#777",
                            cursor: "pointer",
                            zIndex: 10,
                            lineHeight: "1"
                        }}
                        aria-label="Close"
                    >
                        ×
                    </button>


                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: user && user.role === "admin" ? "flex-start" : "center",
                            marginBottom: "18px",
                            marginTop: "12px",
                            marginRight: user && user.role === "admin" ? "30px" : "0px"
                        }}
                    >
                        {user && user.role === "admin" && (
                            <div
                                onClick={() => {
                                    loadUsers();
                                    setShowAdminPanel(true);
                                }}
                                style={{
                                    cursor: "pointer",
                                    fontSize: "30px",
                                    color: "#007bff",
                                    marginTop: "15px",
                                }}
                            >
                                👤
                            </div>
                        )}

                        <h1 style={{ marginBottom: "0px", color: "#007bff", textAlign: "center", width: "100%" }}>
                            Map list
                        </h1>
                    </div>



                    <button
                        onClick={() => {
                            navigate("/live_ais_data");
                            setIsSidebarOpen(false);
                        }}
                        style={{
                            ...buttonStyleSidebar,
                            backgroundColor: (
                                location.pathname === "/live_ais_data" ||
                                location.pathname === "/"
                            ) ? "#0056b3" : "#1485FF",
                            marginBottom: "10px"
                        }}
                    >
                        🚢 Live vessel
                    </button>

                    <button
                        onClick={() => {
                            navigate("/ais_past_data");
                            setIsSidebarOpen(false);
                        }}
                        style={{
                            ...buttonStyleSidebar,
                            backgroundColor: location.pathname === "/ais_past_data" ? "#0056b3" : "#1485FF",
                            marginBottom: "10px"
                        }}
                    >
                        📅 Historical data
                    </button>





                    <button
                        onClick={() => {
                            navigate("/sel_ais_data_all");
                            setIsSidebarOpen(false);
                        }}
                        style={{
                            ...buttonStyleSidebar,
                            backgroundColor: location.pathname === "/sel_ais_data_all" ? "#0056b3" : "#1485FF"
                        }}
                    >
                        Sound exposure level
                    </button>
                    <hr style={{ margin: "15px 0" }} />
                    {!user ? (
                        <button
                            onClick={openLogin}
                            style={{ ...buttonStyleSidebar, backgroundColor: "#00a", marginBottom: "10px" }}
                        >
                            Login/Register
                        </button>

                    ) : (
                        <button
                            onClick={() => {
                                sessionStorage.removeItem("jwtToken");
                                sessionStorage.removeItem("user");


                                setIsLoggedIn(false);

                                setShowLoginModal(false);
                                setAuthMode("login");
                                setMapKey(k => k + 1);

                            }}

                            style={{ ...buttonStyleSidebar, backgroundColor: "#b33", marginBottom: "10px" }}
                        >
                            Logout
                        </button>

                    )}


                    <button
                        onClick={() => window.open('https://www.sciencedirect.com/science/article/pii/S0964569126002954?via%3Dihub', '_blank')}
                        style={{ ...buttonStyleSidebar, backgroundColor: "#198754" }}
                    >
                        📄 Documentation
                    </button>

                    <div style={{ flex: 1 }} />

                    <div style={{ marginTop: "auto", paddingTop: 12 }}>
                        <a
                            href="https://marsci.haifa.ac.il/en/school/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src={`${process.env.PUBLIC_URL}/CSMS.jpeg`}
                                alt="Leon H. Charney School of Marine Sciences"
                                style={{
                                    width: "100%",
                                    height: "auto",
                                    display: "block",
                                    borderRadius: "8px",
                                    marginBottom: "10px",
                                }}
                            />
                        </a>
                        <div
                            style={{
                                position: "relative",
                                marginBottom: "10px",
                            }}
                        >

                            <div
                                style={{
                                    backgroundColor: "#fff",
                                    borderRadius: "8px",
                                    width: "100%",
                                    height: "100%",
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    zIndex: 1,
                                }}
                            ></div>
                            <a
                                href="https://marble.eu/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/marble_logo.png`}
                                    alt="Marble Logo"
                                    style={{
                                        width: "100%",
                                        height: "auto",
                                        display: "block",
                                        borderRadius: "8px",
                                        position: "relative",
                                        marginTop: "-10px",
                                        zIndex: 2,

                                    }}
                                />
                            </a>
                        </div>


                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                marginTop: "-48px",
                            }}
                        >
                            <a
                                href="https://sites.google.com/edu.haifa.ac.il/anl"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/ANL.png`}
                                    alt="ANL - Haifa"
                                    style={{ height: "140px", objectFit: "cover", objectPosition: "center top" }}
                                />
                            </a>
                            <a
                                href="https://uwin-labust.fer.hr/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/UWIN.png`}
                                    alt="ANL - Haifa"
                                    style={{ height: "140px", objectFit: "cover", objectPosition: "center top" }}
                                />
                            </a>

                        </div>
                    </div>


                </div>
            )}
            {showCalcModal && (
                <div
                    style={{
                        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(0,0,0,0.4)", zIndex: 5001,
                        display: "flex", alignItems: "center", justifyContent: "center"
                    }}
                    onClick={() => setShowCalcModal(false)}
                >
                    <form
                        onSubmit={e => {
                            e.preventDefault();

                            if (
                                !selHeatmapGeoJSON ||
                                !selHeatmapGeoJSON.features ||
                                selHeatmapGeoJSON.features.length === 0
                            ) {
                                alert("⚠️ No SEL heatmap generated! Please generate SEL first.");
                                return;
                            }

                            handleCalculate();
                        }}

                        onClick={e => e.stopPropagation()}
                        style={{
                            position: "relative",
                            background: "#fff",
                            padding: "32px 28px",
                            borderRadius: "14px",
                            boxShadow: "0 6px 24px rgba(0,0,0,0.21)",
                            minWidth: 340,
                            display: "flex", flexDirection: "column", gap: 16
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => setShowCalcModal(false)}
                            style={{
                                position: "absolute",
                                top: "18px",
                                right: "18px",
                                background: "transparent",
                                border: "none",
                                fontSize: "26px",
                                fontWeight: "bold",
                                color: "#777",
                                cursor: "pointer",
                                zIndex: 10,
                                lineHeight: "1"
                            }}
                            aria-label="Close"
                        >
                            ×
                        </button>

                        <h2 style={{ margin: 0, marginBottom: 12, textAlign: "center" }}>Calculate SEL</h2>
                        <input
                            type="number"
                            step="any"
                            placeholder="Latitude"
                            value={calcLat}
                            onChange={e => setCalcLat(e.target.value)}
                            required
                            style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                        />
                        <input
                            type="number"
                            step="any"
                            placeholder="Longitude"
                            value={calcLng}
                            onChange={e => setCalcLng(e.target.value)}
                            required
                            style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                        />
                        <input
                            type="number"
                            step="any"
                            placeholder="Depth (m)"
                            value={calcDepth}
                            onChange={e => setCalcDepth(e.target.value)}
                            required
                            autoComplete="off"
                            style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                        />
                        {depthError && (
                            <div style={{ color: "red", fontSize: 14, textAlign: "center" }}>
                                {depthError}
                            </div>
                        )}

                        <button type="submit" style={{ ...buttonStyle, width: "100%" }}>Calculate</button>

                        {calcResult && (
                            <div style={{ marginTop: 18, fontSize: 16 }}>
                                <div>
                                    <b>SEL:</b>{" "}
                                    {Number.isFinite(calcResult.sel)
                                        ? `${calcResult.sel.toFixed(2)} dB re 1 μPa²·s`
                                        : "Too far from vessel"}
                                </div>
                            </div>
                        )}

                    </form>
                </div>
            )}


            {(isLoading || isExporting) && (
                <div style={{
                    position: "fixed",
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(255,255,255,0.75)",
                    zIndex: 3001,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "wait"

                }}>
                    <div style={{
                        width: "70px",
                        height: "70px",
                        border: "8px solid #f3f3f3",
                        borderTop: "8px solid #007bff",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        marginBottom: 20
                    }} />
                    <style>{`
            @keyframes spin {
                0% { transform: rotate(0deg);}
                100% { transform: rotate(360deg);}
            }
        `}</style>
                    <div style={{
                        fontWeight: "bold",
                        fontSize: "20px",
                        color: "#007bff",
                        marginBottom: "10px",
                        textAlign: "center"
                    }}>
                        {isExporting ? "Preparing CSV for download..." : "Generating noise map..."}
                        <div style={{ fontWeight: "normal", fontSize: "13px", color: "#007bff", marginTop: "5px" }}>
                            This may take a bit longer due to the large amount of data.
                        </div>
                    </div>
                    <button
                        style={{
                            marginTop: "10px",
                            padding: "15px 32px",
                            borderRadius: "8px",
                            fontSize: "18px",
                            backgroundColor: "#b33",
                            color: "white",
                            border: "none",
                            fontWeight: "bold",
                            cursor: "pointer",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.18)"
                        }}
                        onClick={handleCancelAll}
                    >
                        Cancel
                    </button>
                </div>
            )}





            <div style={{
                position: "absolute",
                width: "1000px",
                top: 20,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1000,
                background: "rgba(248, 249, 250, 0.9)",
                padding: "20px",
                borderRadius: "10px",
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
                fontFamily: "Arial, sans-serif",
                color: "#333"
            }}>
                {activeTab === "controls1" && (
                    <>
                        <h1 style={{
                            textAlign: "left",
                            fontWeight: 2000,
                            fontSize: "1.8rem",
                            letterSpacing: "0.5px",
                            marginBottom: "12px",
                            color: "#155fa0",
                            textShadow: "0 1px 4px #e7ecf7, 0 1px 0 #fff",
                            marginTop: "0px"
                        }}>
                            Sound exposure level AIS data
                        </h1>

                        <div style={{ display: "flex", justifyContent: "left", gap: "10px", marginBottom: "15px" }}>
                            <button
                                onClick={() => setActiveTab("controls1")}
                                style={{ ...buttonStyle, backgroundColor: activeTab === "controls1" ? "#0056b3" : "#1485FF" }}
                            >
                                Historical AIS data
                            </button>
                            <button
                                onClick={() => setActiveTab("controls2")}
                                style={{ ...buttonStyle, backgroundColor: activeTab === "controls2" ? "#0056b3" : "#1485FF" }}
                            >
                                Upload AIS (CSV format)                            </button>
                        </div>

                        <div style={{ display: "flex", justifyContent: "left", alignItems: "center", gap: "10px", margin: "20px 0" }}>
                            <label>From:</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={(newDate) => {
                                    const resetDates = () => {
                                        setFromDate(null);
                                        setToDate(null);
                                    };

                                    if (toDate && !validateDateRange(newDate, toDate, user, resetDates)) return;

                                    setFromDate(newDate);
                                }}
                                dateFormat="dd/MM/yyyy"
                                maxDate={toDate || new Date()}
                                filterDate={(date) => {
                                    const key = toUTCDateKey(new Date(Date.UTC(
                                        date.getFullYear(), date.getMonth(), date.getDate()
                                    )));
                                    return availableDaysSet.has(key);
                                }}
                            />
                            <label>To:</label>
                            <DatePicker
                                selected={toDate}
                                onChange={(newDate) => {
                                    const resetDates = () => {
                                        setFromDate(null);
                                        setToDate(null);
                                    };

                                    if (fromDate && !validateDateRange(fromDate, newDate, user, resetDates)) return;

                                    setToDate(newDate);
                                }}


                                dateFormat="dd/MM/yyyy"
                                minDate={fromDate}
                                maxDate={new Date()}
                                filterDate={(date) => {
                                    const key = toUTCDateKey(new Date(Date.UTC(
                                        date.getFullYear(), date.getMonth(), date.getDate()
                                    )));
                                    return availableDaysSet.has(key);
                                }}
                            />


                            <button
                                onClick={() => {
                                    if (!fromDate || !toDate) {
                                        alert("Please select dates first!");
                                        return;
                                    }

                                    if (user?.role === "user") {
                                        const months = diffInMonths(fromDate, toDate);
                                        if (months > 2) {
                                            alert("As a user, you can generate SEL data for a maximum range of 2 months.");
                                            return null;
                                        }
                                    }


                                    setPendingSelCsvGeneration(false);
                                    setPendingSelExport(false);
                                    setPendingSelGeneration(true);
                                    setIsSelectingArea(true);
                                    setShowSelectionModal(true);

                                    const map = mapRef.current?.getMap?.();
                                    if (map) map.getCanvas().style.cursor = "crosshair";
                                }}

                                style={buttonStyle}
                            >
                                Generate SEL noisemap
                            </button>

                            <button
                                onClick={() => {

                                    if (!hasSelData) {
                                        alert("No SEL data available! Please generate SEL noisemap first.");
                                        return;
                                    }
                                    if (!isLoggedIn) {
                                        alert("You must be logged in to use this option.");
                                        return;
                                    }
                                    handleExportSelClick();
                                }}
                                style={{
                                    ...buttonStyle,
                                    backgroundColor: hasSelData && isLoggedIn ? "#28a745" : "#9e9e9e",
                                    cursor: hasSelData && isLoggedIn ? "pointer" : "not-allowed",
                                    marginLeft: "15px"
                                }}
                            >
                                📤 Export SEL
                            </button>
                            {user?.role === "admin" && (
                                <button
                                    onClick={async () => {
                                        if (!fromDate || !toDate) return alert("No SEL data available! Please generate SEL noisemap first.");
                                        if (
                                            fromDate.getFullYear() !== toDate.getFullYear() ||
                                            fromDate.getMonth() !== toDate.getMonth() ||
                                            fromDate.getDate() !== toDate.getDate()
                                        ) {
                                            alert("CSV export is only allowed for a single day.");
                                            return;
                                        }

                                        setIsExporting(true);
                                        try {
                                            const from = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, "0")}-${String(fromDate.getDate()).padStart(2, "0")} 00:00:00`;
                                            const to = `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, "0")}-${String(toDate.getDate()).padStart(2, "0")} 23:59:59`;
                                            const url = `${API_BASE}/api/export-csv?from=${from}&to=${to}`;
                                            const response = await fetch(url);
                                            const blob = await response.blob();
                                            const link = document.createElement("a");
                                            link.href = window.URL.createObjectURL(blob);
                                            const pad = (n) => n.toString().padStart(2, "0");
                                            const fromDateString = `${pad(fromDate.getDate())}-${pad(fromDate.getMonth() + 1)}-${fromDate.getFullYear()}`;
                                            const toDateString = `${pad(toDate.getDate())}-${pad(toDate.getMonth() + 1)}-${toDate.getFullYear()}`;
                                            link.download = `AIS_export_${fromDateString}_to_${toDateString}.csv`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        } catch (e) {
                                            alert("Something is wrong with the CSV export!");
                                        } finally {
                                            setIsExporting(false);
                                        }
                                    }}
                                    style={{
                                        ...buttonStyle,
                                        backgroundColor: "#28a745",
                                        marginLeft: "15px"
                                    }}
                                >
                                    📤 Export AIS data
                                </button>


                            )}







                        </div>



                        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "0px" }}>
                            <div style={{ display: "flex", gap: "10px" }}>

                            </div>
                        </div>
                    </>
                )}

                {activeTab === "controls2" && (
                    <>
                        <h1 style={{
                            textAlign: "left",
                            fontWeight: 2000,
                            fontSize: "1.8rem",
                            letterSpacing: "0.5px",
                            marginBottom: "12px",
                            color: "#155fa0",
                            textShadow: "0 1px 4px #e7ecf7, 0 1px 0 #fff",
                            marginTop: "0px"
                        }}>
                            Sound exposure level CSV data
                        </h1>

                        <div style={{ display: "flex", justifyContent: "left", gap: "10px", marginBottom: "15px" }}>
                            <button
                                onClick={() => setActiveTab("controls1")}
                                style={{ ...buttonStyle, backgroundColor: activeTab === "controls1" ? "#0056b3" : "#1485FF" }}
                            >
                                Historical AIS data
                            </button>
                            <button
                                onClick={() => setActiveTab("controls2")}
                                style={{ ...buttonStyle, backgroundColor: activeTab === "controls2" ? "#0056b3" : "#1485FF" }}
                            >
                                Upload AIS (CSV format)
                            </button>
                        </div>

                        <div style={{ marginBottom: 15 }}>
                            <label style={{ fontWeight: "bold", marginRight: 8 }}>Load downloaded CSV:</label>
                            <input type="file" accept=".csv" onChange={handleFileUpload} style={{ marginRight: 12 }} />
                        </div>

                        <div style={{ display: "flex", justifyContent: "left", alignItems: "center", gap: "10px", margin: "20px 0" }}>
                            <label>From:</label>
                            <div style={{ position: "relative", display: "inline-block" }}>
                                <DatePicker
                                    selected={fromDate}
                                    onChange={(newDate) => {
                                        const resetDates = () => {
                                            setFromDate(null);
                                            setToDate(null);
                                        };

                                        if (toDate && !validateDateRange(newDate, toDate, user, resetDates)) return;

                                        setFromDate(newDate);
                                    }}
                                    dateFormat="dd/MM/yyyy"
                                    maxDate={toDate || new Date()}
                                    filterDate={(date) => {
                                        const key = toUTCDateKey(new Date(Date.UTC(
                                            date.getFullYear(), date.getMonth(), date.getDate()
                                        )));
                                        return availableCsvDaysSet.has(key);
                                    }}

                                />

                                {!csvData || csvData.length === 0 ? (
                                    <div
                                        onClick={() => alert("Please Upload AIS (CSV format) first!")}
                                        style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, cursor: "not-allowed", background: "rgba(255,255,255,0.3)" }}
                                    />
                                ) : null}
                            </div>

                            <label>To:</label>
                            <div style={{ position: "relative", display: "inline-block" }}>
                                <DatePicker
                                    selected={toDate}
                                    onChange={(newDate) => {
                                        const resetDates = () => {
                                            setFromDate(null);
                                            setToDate(null);
                                        };

                                        if (fromDate && !validateDateRange(fromDate, newDate, user, resetDates)) return;

                                        setToDate(newDate);
                                    }}


                                    dateFormat="dd/MM/yyyy"
                                    minDate={fromDate}
                                    maxDate={new Date()}
                                    filterDate={(date) => {
                                        const key = toUTCDateKey(new Date(Date.UTC(
                                            date.getFullYear(), date.getMonth(), date.getDate()
                                        )));
                                        return availableCsvDaysSet.has(key);
                                    }}

                                />

                                {!csvData || csvData.length === 0 ? (
                                    <div
                                        onClick={() => alert("Please Upload AIS (CSV format) first!")}
                                        style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, cursor: "not-allowed", background: "rgba(255,255,255,0.3)" }}
                                    />
                                ) : null}
                            </div>


                            <button onClick={handleGenerateCsvSelClick} style={buttonStyle}>
                                Generate SEL noisemap
                            </button>

                            <button
                                onClick={() => {

                                    if (!hasSelData) {
                                        alert("No SEL data available! Please generate SEL noisemap first.");
                                        return;
                                    }
                                    if (!isLoggedIn) {
                                        alert("You must be logged in to use this option.");
                                        return;
                                    }
                                    handleExportSelClick();
                                }}
                                style={{
                                    ...buttonStyle,
                                    backgroundColor: hasSelData && isLoggedIn ? "#28a745" : "#9e9e9e",
                                    cursor: hasSelData && isLoggedIn ? "pointer" : "not-allowed",
                                    marginLeft: "15px"
                                }}
                            >
                                📤 Export SEL
                            </button>

                            {user?.role === "admin" && (
                                <button
                                    onClick={async () => {
                                        if (!fromDate || !toDate) return alert("No SEL data available! Please generate SEL noisemap first.");
                                        setIsExporting(true);
                                        try {
                                            const from = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, "0")}-${String(fromDate.getDate()).padStart(2, "0")} 00:00:00`;
                                            const to = `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, "0")}-${String(toDate.getDate()).padStart(2, "0")} 23:59:59`;
                                            const url = `${API_BASE}/api/export-csv?from=${from}&to=${to}`;
                                            const response = await fetch(url);
                                            const blob = await response.blob();
                                            const link = document.createElement("a");
                                            link.href = window.URL.createObjectURL(blob);
                                            const pad = (n) => n.toString().padStart(2, "0");
                                            const fromDateString = `${pad(fromDate.getDate())}-${pad(fromDate.getMonth() + 1)}-${fromDate.getFullYear()}`;
                                            const toDateString = `${pad(toDate.getDate())}-${pad(toDate.getMonth() + 1)}-${toDate.getFullYear()}`;
                                            link.download = `AIS_export_${fromDateString}_to_${toDateString}.csv`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        } catch (e) {
                                            alert("Something is wrong with the CSV export!");
                                        } finally {
                                            setIsExporting(false);
                                        }
                                    }}
                                    style={{
                                        ...buttonStyle,
                                        backgroundColor: "#28a745",
                                        marginLeft: "15px"
                                    }}
                                >
                                    📤 Export AIS data
                                </button>

                            )}







                        </div>




                        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "0px" }}>
                            <div style={{ display: "flex", gap: "10px" }}>

                            </div>
                        </div>
                    </>
                )}
            </div>

            <div style={{
                position: "absolute",
                bottom: "41px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 2000,
                backgroundColor: "rgba(255,255,255,0.9)",
                padding: "12px 20px",
                borderRadius: "8px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                display: "flex",
            }}>
                <div>
                    <div style={{ fontWeight: "bold", fontSize: "14px", color: "black", marginBottom: "5px" }}>Frequency Bands:</div>
                    <div style={{ display: "flex", gap: "6px" }}>
                        <button
                            style={{
                                ...buttonStyle,
                                height: "50px",
                                backgroundColor: selectedBand === "63Hz" ? "#0056b3" : "#1485FF",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                lineHeight: "1.2",
                                padding: "6px 12px"
                            }}
                            onClick={() => setSelectedBand("63Hz")}
                        >
                            63 Hz
                        </button>

                        <button
                            style={{
                                ...buttonStyle,
                                height: "50px",
                                backgroundColor: selectedBand === "125Hz" ? "#0056b3" : "#1485FF",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                lineHeight: "1.2",
                                padding: "6px 12px"
                            }}
                            onClick={() => setSelectedBand("125Hz")}
                        >
                            125 Hz
                        </button>

                        <button
                            style={{
                                ...buttonStyle,
                                height: "50px",
                                backgroundColor: selectedBand === "Broadband" ? "#0056b3" : "#1485FF",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                lineHeight: "1.2",
                                padding: "6px 12px"
                            }}
                            onClick={() => setSelectedBand("Broadband")}
                        >
                            Broadband<br />(20–2000 Hz)
                        </button>
                    </div>
                </div>

            </div>
            {isSelectingArea && selectionStart && selectionCoords && (

                <div style={{
                    position: "absolute",
                    bottom: 357,
                    right: 10,
                    zIndex: 1100,
                    background: "rgba(255,255,255,0.95)",
                    padding: "10px 15px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
                    fontSize: "13px",
                    width: 260
                }}>
                    <div style={{ fontWeight: "bold", marginBottom: "8px" }}>Selection area coordinates</div>
                    <div><b>Min Lat:</b> {selectionCoords.minLat}</div>
                    <div><b>Max Lat:</b> {selectionCoords.maxLat}</div>
                    <div><b>Min Lng:</b> {selectionCoords.minLng}</div>
                    <div><b>Max Lng:</b> {selectionCoords.maxLng}</div>
                </div>
            )}

            <div
                style={{
                    position: "absolute",
                    bottom: 287,
                    right: 10,
                    backgroundColor: "white",
                    padding: "12px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
                    zIndex: 1000,
                    width: 170,
                    cursor: "pointer"
                }}
                title="Click to see model info"
            >
                <label htmlFor="model-select" style={{ fontWeight: "bold", color: "black" }}>
                    Model:
                </label>
                <select
                    id="model-select"
                    defaultValue="AVERAGE"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        alert("This display use only the COMBINED model of all models for that vessel type.");
                    }}
                    style={{
                        ...selectBoxStyle,
                        backgroundColor: "#f3f3f3",
                        color: "#444",
                        cursor: "pointer"
                    }}
                >
                    <option value="AVERAGE">COMBINED</option>
                </select>

            </div>

            <div style={{
                position: "absolute",
                bottom: 238,
                right: 10,
                backgroundColor: "white",
                padding: "12px",
                borderRadius: "8px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
                zIndex: 1000,
                width: 170,
                cursor: "pointer",
                fontSize: "14px",
            }}>

                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>


                    <label style={{ cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={showMPA}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setShowMPA(checked);

                                if (checked) {
                                    setMpaLoading(true);
                                } else {
                                    setMpaLoading(false);
                                }
                            }}
                        />

                        Marine protected areas
                    </label>

                </div>
            </div>
            {/*
            <div style={{
                position: "absolute",
                bottom: 171,
                right: 10,
                backgroundColor: "white",
                padding: "12px",
                borderRadius: "8px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
                zIndex: 1000,
                width: 170
            }}>
                <button
                    onClick={() => {
                        setIsPickingLocation(true);
                        setCalcDepth("");
                        setCalcResult(null);
                        setCalcLat("");
                        setCalcLng("");
                        setDepthError("");
                        const map = mapRef.current?.getMap?.();
                        if (map) map.getCanvas().style.cursor = "crosshair";
                        if (pickHintRef.current) pickHintRef.current.style.display = "block";
                    }}
                    style={{ ...buttonStyle, width: "100%", backgroundColor: isPickingLocation ? "green" : buttonStyle.backgroundColor }}
                >
                    Calculate SEL
                </button>
            </div>
           */}
            <div
                ref={pickHintRef}
                style={{
                    display: "none",
                    position: "absolute",
                    top: 80,
                    right: 10,
                    background: "rgba(0,0,0,0.65)",
                    color: "#fff",
                    padding: "8px 10px",
                    borderRadius: 6,
                    zIndex: 1000,
                    fontSize: 13
                }}
            >
                Click on the map to pick a location. (Esc to cancel)
            </div>

            {mpaLoading && (
                <div style={{
                    position: "fixed",
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(255,255,255,0.7)",
                    zIndex: 3000,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "wait"

                }}>
                    <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "25px", color: "#007bff" }}>
                        Loading marine protected areas...
                    </div>

                    <div style={{ fontWeight: "normal", fontSize: "18px", color: "#007bff", marginTop: "5px" }}>
                        This may take a few second.
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(0,0,0,0.4)",
                        zIndex: 7000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                    onClick={() => setShowDeleteModal(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "white",
                            padding: "25px",
                            borderRadius: "12px",
                            width: "350px",
                            textAlign: "center",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
                        }}
                    >
                        <h3 style={{ marginBottom: "20px" }}>
                            Are you sure you want to delete this user?
                        </h3>

                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <button
                                style={{
                                    background: "red",
                                    color: "white",
                                    padding: "10px 20px",
                                    borderRadius: "8px",
                                    border: "none",
                                    cursor: "pointer",
                                    width: "45%"
                                }}
                                onClick={() => {
                                    deleteUser(deleteUserId);
                                    setShowDeleteModal(false);
                                }}
                            >
                                <h3 >
                                    Yes
                                </h3>
                            </button>

                            <button
                                style={{
                                    background: "#ccc",
                                    color: "#000",
                                    padding: "10px 20px",
                                    borderRadius: "8px",
                                    border: "none",
                                    cursor: "pointer",
                                    width: "45%"
                                }}
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <h3 >
                                    No
                                </h3>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showRegisterSuccess && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            padding: "30px",
                            borderRadius: "10px",
                            width: "350px",
                            position: "relative",
                            textAlign: "center",
                            boxShadow: "0 4px 15px rgba(0,0,0,0.3)"
                        }}
                    >
                        <button
                            onClick={() => setShowRegisterSuccess(false)}
                            style={{
                                position: "absolute",
                                top: "18px",
                                right: "18px",
                                background: "transparent",
                                border: "none",
                                fontSize: "23px",
                                fontWeight: "bold",
                                color: "#777",
                                cursor: "pointer",
                                zIndex: 10,
                                lineHeight: "1"
                            }}
                        >
                            ×
                        </button>

                        <h2 style={{ marginBottom: "20px" }}>Successful registration!</h2>

                        <button
                            onClick={() => setShowRegisterSuccess(false)}
                            style={{
                                padding: "3px 25px",
                                backgroundColor: "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontWeight: "bold",
                                marginTop: "5px"
                            }}
                        >
                            <h2> OK</h2>
                        </button>
                    </div>
                </div>
            )}


            <Map
                key={mapKey}
                ref={mapRef}
                mapLib={import("maplibre-gl")}
                onClick={async (e) => {
                    if (isPickingLocation) {
                        const { lng, lat } = e.lngLat;
                        setCalcLat(lat.toFixed(6));
                        setCalcLng(lng.toFixed(6));
                        setCalcDepth("");
                        setCalcResult(null);
                        setDepthError("");

                        fetch(`${API_BASE}/api/depths`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify([{ lat, lon: lng }])
                        })
                            .then(res => res.json())
                            .then(data => {
                                if (data.depths && data.depths[0] !== null) {
                                    setActualDepth(data.depths[0]);
                                } else {
                                    setActualDepth(null);
                                }
                            })
                            .catch(err => {
                                console.error("Depth fetch error:", err);
                                setActualDepth(null);
                            });

                        setIsPickingLocation(false);
                        const map = mapRef.current?.getMap?.();
                        if (map) map.getCanvas().style.cursor = "";
                        if (pickHintRef.current) pickHintRef.current.style.display = "none";
                        setShowCalcModal(true);
                        return;
                    }

                    if (isSidebarOpen) setIsSidebarOpen(false);



                    if (!isSelectingArea) return;

                    const { lng, lat } = e.lngLat;

                    setShowSelectionModal(false);

                    if (!selectionStart) {
                        setSelectionStart({ lng, lat });
                        setSelectionEnd(null);
                        return;
                    }

                    let endLng = lng;
                    let endLat = lat;

                    if (!isLoggedIn && selectionStart) {
                        const maxLat = selectionStart.lat + 2.0;
                        const minLat = selectionStart.lat - 2.0;
                        const maxLng = selectionStart.lng + 2.0;
                        const minLng = selectionStart.lng - 2.0;

                        endLat = Math.min(Math.max(endLat, minLat), maxLat);
                        endLng = Math.min(Math.max(endLng, minLng), maxLng);
                    }


                    setSelectionEnd({ lng: endLng, lat: endLat });
                    setIsSelectingArea(false);

                    const map = mapRef.current?.getMap?.();
                    if (map) map.getCanvas().style.cursor = "";

                    const bounds = {
                        minLat: Math.min(selectionStart.lat, endLat),
                        maxLat: Math.max(selectionStart.lat, endLat),
                        minLng: Math.min(selectionStart.lng, endLng),
                        maxLng: Math.max(selectionStart.lng, endLng),
                    };

                    const latSpan = bounds.maxLat - bounds.minLat;
                    const lngSpan = bounds.maxLng - bounds.minLng;

                    if (!isLoggedIn && (latSpan > 3.00001 || lngSpan > 3.00001)) {

                        setIsSelectingArea(false);
                        setSelectionStart(null);
                        setSelectionEnd(null);
                        setSelectionCoords(null);
                        if (map) map.getCanvas().style.cursor = "";
                        return;
                    }

                    const finalBounds = {
                        minLat: bounds.minLat,
                        maxLat: bounds.maxLat,
                        minLng: bounds.minLng,
                        maxLng: bounds.maxLng
                    };

                    if (pendingSelGeneration) {
                        handleGenerateSelWithBounds(finalBounds);
                    }
                    else if (pendingSelExport) {
                        handleExportSelWithBounds(finalBounds);
                    }
 else if (pendingCsvFilter) {

    const sourceBounds = expandBoundsByMeters(
        finalBounds,
        getSelMaxRangeMeters(selectedBand)
    );

    const filtered = csvData.filter(row => {
        const lat = Number(row.LATITUDE);
        const lon = Number(row.LONGITUDE);

        const inBounds =
            lat >= sourceBounds.minLat &&
            lat <= sourceBounds.maxLat &&
            lon >= sourceBounds.minLng &&
            lon <= sourceBounds.maxLng;

        const inDates = isRowWithinSelectedDates(row, fromDate, toDate);

        return inBounds && inDates;
    });

                        await fetchSelHeatmapFromCsv(filtered, finalBounds, 0.02);

                        setPendingCsvFilter(false);
                    }
                    else if (pendingSelCsvGeneration) {

    const sourceBounds = expandBoundsByMeters(
        finalBounds,
        getSelMaxRangeMeters(selectedBand)
    );

    const filteredCsv = csvData.filter(row => {
        const lat = Number(row.LATITUDE);
        const lon = Number(row.LONGITUDE);

        const inBounds =
            lat >= sourceBounds.minLat &&
            lat <= sourceBounds.maxLat &&
            lon >= sourceBounds.minLng &&
            lon <= sourceBounds.maxLng;

        const inDates = isRowWithinSelectedDates(row, fromDate, toDate);

        return inBounds && inDates;
    });

    await fetchSelHeatmapFromCsv(filteredCsv, finalBounds, 0.02);

    setPendingSelCsvGeneration(false);
}


                    setSelectionStart(null);
                    setSelectionEnd(null);
                    setSelectionCoords(null);



                }}


                initialViewState={{
                    longitude: 25.9,
                    latitude: 40.72,
                    zoom: 4,
                    maxZoom: 8,
                    minZoom: 3.5
                }}
                style={{ flex: 1, width: "100%" }}
                mapStyle="https://api.maptiler.com/maps/0195ec7d-c124-7e8f-85b1-bb0602899c0e/style.json?key=sDvAXBg4VEXfXMSAABi9"

                onLoad={() => {
                    const map = mapRef.current.getMap();

                    const layers = map.getStyle().layers;

                    if (layers.find(layer => layer.id === 'Depth labels')) {
                        map.moveLayer('Depth labels');
                    } else {
                        console.log("Layer 'Depth labels' does not exist yet.");
                    }
                    if (layers.find(layer => layer.id === 'Terrain RGB')) {
                        map.moveLayer('Terrain RGB');
                    } else {
                        console.log("Layer 'Terrain RGB' does not exist yet.");
                    }



                    if (layers.find(layer => layer.id === 'Lake')) {
                        map.moveLayer('Lake');
                    } else {
                        console.log("Layer 'Lake' does not exist yet.");
                    }

                    if (layers.find(layer => layer.id === 'Rivers')) {
                        map.moveLayer('Rivers');
                    } else {
                        console.log("Layer 'Rivers' does not exist yet.");
                    }

                    if (layers.find(layer => layer.id === 'Island labels')) {
                        map.moveLayer('Island labels');
                    } else {
                        console.log("Layer 'Island labels' does not exist yet.");
                    }

                }}
                onMove={(e) => {
                    const newZoom = e.viewState.zoom;
                    setZoomLevel(parseFloat(newZoom.toFixed(2)));
                }}




                onMouseMove={(e) => {
                    if (!isSelectingArea || !selectionStart) return;

                    let newLng = e.lngLat.lng;
                    let newLat = e.lngLat.lat;

                    if (!isLoggedIn) {
                        const maxLat = selectionStart.lat + 2.0;
                        const minLat = selectionStart.lat - 2.0;
                        const maxLng = selectionStart.lng + 2.0;
                        const minLng = selectionStart.lng - 2.0;

                        newLat = Math.min(Math.max(newLat, minLat), maxLat);
                        newLng = Math.min(Math.max(newLng, minLng), maxLng);
                    }

                    if (user?.role === "user") {
                        const maxLat = selectionStart.lat + 10.0;
                        const minLat = selectionStart.lat - 10.0;
                        const maxLng = selectionStart.lng + 10.0;
                        const minLng = selectionStart.lng - 10.0;

                        newLat = Math.min(Math.max(newLat, minLat), maxLat);
                        newLng = Math.min(Math.max(newLng, minLng), maxLng);
                    }


                    const end = { lng: newLng, lat: newLat };
                    setSelectionEnd(end);

                    setSelectionCoords({
                        minLat: Math.min(selectionStart.lat, end.lat).toFixed(5),
                        maxLat: Math.max(selectionStart.lat, end.lat).toFixed(5),
                        minLng: Math.min(selectionStart.lng, end.lng).toFixed(5),
                        maxLng: Math.max(selectionStart.lng, end.lng).toFixed(5),
                    });
                }}






            >





                {selectedShip && (
                    <Popup
                        longitude={selectedShip.LONGITUDE}
                        latitude={selectedShip.LATITUDE}
                        anchor="top"
                        closeOnClick={false}
                        onClose={() => setSelectedShip(null)}
                    >
                        <div>
                            <strong>{selectedShip.NAME || "Unknown"}</strong><br />
                            MMSI: {selectedShip.MMSI}<br />
                            Name: {selectedShip.NAME}<br />
                            Speed: {selectedShip.SPEED} knots<br />
                            Course: {selectedShip.COURSE}°<br />
                            NAVSTAT: {selectedShip.NAVSTAT}<br />

                            Vessel type: {getShipDescription(selectedShip.AISTYPE)}<br />
                            Time: {selectedShip[timeColumn]}
                            {selectedShip.AISTYPE === 36 && selectedShip.NAVSTAT === 0 && selectedShip.SPEED > 2.13 && (
                                <>
                                    <br />
                                    <span>Noise:
                                        {(() => {

                                            const noise = calculateNoiseByModel(selectedShip, selectedModel, selectedBand);
                                            return `${noise.toFixed(2)} dB (${selectedModel}, ${selectedBand})`;

                                        })()}
                                    </span>
                                </>
                            )}
                        </div>
                    </Popup>
                )}
                <div>
                </div>







                <Source
                    id="mask-land"
                    type="vector"
                    tiles={["https://api.maptiler.com/tiles/land/{z}/{x}/{y}.pbf?key=sDvAXBg4VEXfXMSAABi9"]}
                >
                    <Layer
                        id="mask-land-fill"
                        source="mask-land"
                        source-layer="land"
                        type="fill"
                        paint={{
                            "fill-color": "#E0E0E0",
                            "fill-opacity": 0.9999999999999
                        }}
                    />
                </Source>






{showHeatmapLegend && (
    <div
        style={{
            position: "absolute",
            bottom: "40px",
            right: "10px",
            backgroundColor: "rgb(255, 255, 255)",
            padding: "12px",
            borderRadius: "10px",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.3)",
            zIndex: 1000,
            fontSize: "14px",
            width: "280px",
        }}
    >
        <h4 style={{ textAlign: "center", marginBottom: "12px", fontSize: "13px" }}>
            Sound Exposure Level (SEL, dB re 1 μPa²·s)
        </h4>

        <div
            style={{
                width: "100%",
                height: "20px",
                background: `linear-gradient(to right,
                    ${selColorStops[1]} 0%,
                    ${selColorStops[3]} 20%,
                    ${selColorStops[5]} 40%,
                    ${selColorStops[7]} 70%,
                    ${selColorStops[9]} 90%,
                    ${selColorStops[11]} 100%)`,
                borderRadius: "5px",
                marginBottom: "6px"
            }}
        />

        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "11px",
                color: "#333",
                fontWeight: "bold"
            }}
        >
            {legendTicks.map((tick, i) => (
                <span key={i}>{tick}</span>
            ))}
        </div>
    </div>
)}

                {selHeatmapGeoJSON && (
                    <Source id="sel-heatmap" type="geojson" data={selHeatmapGeoJSON}>
                        <Layer
                            id="sel-points"
                            type="circle"
                            beforeId="mask-land-fill"
                            paint={{
                                "circle-radius": [
                                    "interpolate", ["linear"], ["zoom"],
                                    4, 8,
                                    5, 10,
                                    6, 12,
                                    7, 14,
                                    8, 16,
                                    9, 18
                                ],
                                "circle-color": [
    "interpolate", ["linear"], ["get", "sel"],
    ...selColorStops
],
                            "circle-opacity": 0.78,
"circle-blur": 0.75,   
                                "circle-stroke-width": 0
                            }}
                        />
                    </Source>
                )}



                {selectionStart && selectionEnd && (
                    <Source
                        id="selection-rect"
                        type="geojson"
                        data={{
                            type: "FeatureCollection",
                            features: [{
                                type: "Feature",
                                geometry: {
                                    type: "Polygon",
                                    coordinates: [[
                                        [selectionStart.lng, selectionStart.lat],
                                        [selectionEnd.lng, selectionStart.lat],
                                        [selectionEnd.lng, selectionEnd.lat],
                                        [selectionStart.lng, selectionEnd.lat],
                                        [selectionStart.lng, selectionStart.lat]
                                    ]]
                                }
                            }]
                        }}
                    >
                        <Layer
                            id="selection-rect-fill"
                            type="fill"
                            paint={{
                                "fill-color": "rgba(255, 80, 80, 0.25)"
                            }}
                        />

                        <Layer
                            id="selection-rect-border"
                            type="line"
                            paint={{
                                "line-color": "rgba(200, 30, 30, 1.0)",
                                "line-width": 1.25
                            }}
                        />
                    </Source>
                )}

                {showMPA && (
                    <Source
                        id="mpa"
                        type="geojson"
                        data={process.env.PUBLIC_URL + "/mpa/merged_mpa_global_marine.geojson"}
                    >
                        <Layer
                            id="mpa-outline"
                            type="line"
                            paint={{
                                "line-color": "#000000",
                                "line-width": 2,
                                "line-dasharray": [2, 2],
                                "line-opacity": 0.9
                            }}
                        />
                    </Source>
                )}

<div
    style={{
        position: "absolute",
        bottom: "20px",
        left: "20px",
        zIndex: 30
    }}
>
    <ScaleControl
        position="bottom-left"
        maxWidth={120}
        unit="metric"
    />
</div>

            </Map>




        </div>
    );
};

export default ShipMap;




